import { PureComponent } from 'react';
import { Typography } from '@mui/material';
import GeometryType from 'ol/geom/GeometryType';
import { FaNfcDirectional } from 'react-icons/fa6';
import { MdOutlineDonutSmall } from 'react-icons/md';
import Collection from 'ol/Collection';
import { GREEN, RED, BROWN, PLUM, BURLY_WOOD, LIGHT_BLUE, YELLOW, BLACK } from '../../rgbColors';
import {
  SampleChangeType,
  CoordinatePoint,
  DRAW_TYPES,
  JobType,
  OnLoading,
  SampleSource,
  eventIsDrawEvent,
  ChangeReason,
  SetWaypointFunction,
  SetZoneWaypointFunction,
} from '../../types/types';
import { DiCelluloid } from 'react-icons/di';
import { IoMdAddCircleOutline } from 'react-icons/io';
import CenterMissionButton from './buttons/CenterMissionButton';
import CoordinateDisplayButton from './buttons/CoordinateDisplayButton';
import AssignmentButton from './buttons/AssignmentButton';
import DeselectAllButton from './buttons/DeselectAllButton';
import MapButton from './buttons/MapButton';
import MapInteractionsLayer from './layers/MapInteractionsLayer';
import CorePointsLayer from './layers/CorePointsLayer';
import CentroidsLayer from './layers/CentroidsLayer';
import PathLayer from './layers/PathLayer';
import DumpEventsLayer from './layers/DumpEventsLayer';
import StaticPointsLayer from './layers/StaticPointsLayer';
import ZonesLayer from './layers/ZonesLayer';
import DrawLayer from './layers/DrawLayer';
import DrawButton from './buttons/DrawButton';
import DeleteButton from './buttons/DeleteButton';
import UndoButton from './buttons/UndoButton';
import TechnicianSidebarButton from './buttons/TechnicianSidebarButton';
import TechnicianSidebar from './sidebar/TechnicianSidebar';
import WaypointSetButton from './buttons/WaypointSetButton';
import { KML_FEATURE_TYPE, KmlFeatureType } from '../../featureTypes';
import {
  alertError,
  alertFullScreen,
  alertInfo,
  alertSuccess,
  alertWarn,
  alertWarnConfirm,
} from '../../alertDispatcher';
import { createCheckpoint, loadCheckpoint } from '../../db/checkpoint';
import { GridPatterns, Mission, Sample, SoilCore, Zone, ZoneType } from '../../db';
import Polygon from 'ol/geom/Polygon';
import { Extent } from 'ol/extent';
import { getCurrentMission, getCurrentUser, isCurrentUserOperator } from '../../dataModelHelpers';
import { dispatchMissionBoundaryUpdated, dispatchMissionUpdated, MISSION_EVENTS } from '../../missionEvents';
import {
  SetStateAsync,
  convertProjection3857,
  convertProjection4329,
  isPointDelete,
  normalizeDoubleEncodedJsonString,
  removeConsecutiveDuplicateVertices,
  setStateAsync,
  updateState,
} from '../../utils';
import { calculateArmPosition } from '../../services/RobotArmService';
import EventBus from '../../EventBus';
import { INBOUND_TOPICS, rosEventKey } from '../../types/rosTopics';
import SamplingParametersButton from './buttons/SamplingParametersButton';
import MapOverlay from './overlays/MapOverlay';
import CoordinateDisplayOverlay from './overlays/CoordinateDisplayOverlay';
import CoordinateDisplayLayer from './layers/CoordinateDisplayLayer';
import { MAP_TYPES, MapType } from './helpers/mapTypes';
import {
  SelectCandidate,
  DrawConfig,
  RobotConnectionStatus,
  RobotNavControl,
  SettingsAccessLevel,
} from '../../types/types';
import { RosMissionMsg, RosPosition, StartAt } from '../../types/rosmsg';
import { Coordinate } from 'ol/coordinate';
import { getFeatureType } from './helpers/features';
import { ModifyEvent } from 'ol/interaction/Modify';
import Feature, { FeatureLike } from 'ol/Feature';
import { LineString, Point, SimpleGeometry, Geometry } from 'ol/geom';
import MapLoadingButton from './buttons/MapLoadingButton';
import { FaBarcode } from 'react-icons/fa';
import { BsArrowsMove } from 'react-icons/bs';
import { findSampleById, setSampleSelected } from '../../sampleSelectionHelpers';
import BaseEvent from 'ol/events/Event';
import DrawingMarkersLayer from './layers/DrawingMarkersLayer';
import SkipButton from './buttons/SkipButton';
import { ShowChangeTypeSelectorPopup, promptForChangeReason } from '../../changeReasonHelpers';
import { setSampleBarcodes } from '../../barcodes';
import logger from '../../logger';
import PlotGenerationButton from './buttons/PlotGenerationButton';
import GridLayer, { GridLayerFeature } from './layers/GridLayer';
import GridGenerationButton from './buttons/GridGenerationButton';
import { CoreCountEnforcementEnabled } from '../../db/local_storage';
import {
  dispatchCoresNeedUpdate,
  dispatchSamplesUpdated,
  dispatchStaticPointsUpdated,
  dispatchZonesUpdated,
  dispatchSetCenter,
  dispatchSetExtent,
  MAP_EVENTS,
} from '../../mapEvents';
import { GiHazardSign, GiMultipleTargets } from 'react-icons/gi';
import { MdLocationSearching } from 'react-icons/md';
import { PiRectangle } from 'react-icons/pi';
import { BiPolygon, BiRename } from 'react-icons/bi';
import { ManualMeasurementDialogParameters } from '../../services/ManualMeasurementsChecker';
import CoresDialog from './dialogs/CoresDialog';
import RotateCoreLineDialog from './dialogs/RotateCoreLineDialog';
import { canRotateCoreLine } from './helpers/rotateCoreLineRules';
import { TbRotateClockwise, TbShovel } from 'react-icons/tb';
import CoordinateInput from './CoordinateInput';
import { fromLonLat } from 'ol/proj';
import { ZONE_TYPE_TO_KML_FEATURE_TYPE } from '../../db/ZoneTypesDatatype';
import { polygonEraser } from '../../services/zones/polygon-eraser/PolygonEraser';
import zonesManager from '../../services/zones/ZonesManager';
import { BaseMapWithHistoricalLayers } from './BaseMapWithHistoricalLayers';
import { HistoricalLayersService } from '../../types/historical-layers';
import LassoService, { LassoSampleAction } from '../../services/LassoService';
import LassoActionDialog from './dialogs/LassoActionDialog';
import LassoMoveClickLayer from './layers/LassoMoveClickLayer';

const featureIsSkippedOrDeleted = (feature: Feature<Geometry>) => {
  const properties = feature.getProperties();

  if ('change_type' in properties) {
    const changeType = properties.change_type;

    return changeType === 'Skip' || changeType === 'Delete';
  }

  return false;
};

/**
 * Common Map Functionality between the sampling map and map maker map
 */
interface CommonMapProps {
  type: MapType;
  buttons: React.ReactNode[];
  statusItems?: React.ReactNode[];
  primaryActions?: React.ReactNode[];
  layers: React.ReactNode[];
  absoluteOverlays?: React.ReactNode[];
  secondRowElements?: React.ReactNode[];
  rotation?: { value: number };
  setWaypoint?: SetWaypointFunction;
  setZoneWaypoint?: SetZoneWaypointFunction;
  fit?: { value: Extent };
  // zoom?: { value: number };
  center?: { value: Coordinate };
  robotMission?: RosMissionMsg;
  connectionStatus?: RobotConnectionStatus;
  trackingButton?: React.ReactNode;
  accessLevel: SettingsAccessLevel;
  robotNavControlState?: RobotNavControl;
  onLoading: OnLoading;
  missionActive: string;
  openManualMeasurementDialog?: (manualMeasurementDialogParameters: ManualMeasurementDialogParameters) => void;
  drawButtonPopperOffset?: [number, number]; // Optional offset for DrawButton Popper panel
  showTimeline?: boolean;
  /**
   * Optional historical layers service for dependency injection
   */
  historicalLayersService?: HistoricalLayersService;
}

interface CommonMapState {
  coreLine: Coordinate[];
  center?: { value: Coordinate };
  fit?: { value: Extent };
  zoom?: { value: number };
  rotation?: { value: number };
  deleteCandidate: SelectCandidate | null;
  zoneTypeChangeCandidate: SelectCandidate | null;
  skipCandidate: SelectCandidate | null;
  waypointCandidate: SelectCandidate | null;
  moveCandidate: SelectCandidate | null;
  drawConfig: DrawConfig;
  addSampleActive: Coordinate | null;
  parkingPointCandidate: Coordinate | null;
  startingPointCandidate: Coordinate | null;
  endPointCandidate: Coordinate | null;
  pullinPointCandidate: Coordinate | null;
  selectedSample: SelectCandidate | null;
  reorderLine: Coordinate[];
  selectedReorder: Coordinate[];
  renumber: boolean;
  checkpoints: number[];
  zoneMission: boolean;
  jobType: JobType;
  settingWaypoint: boolean;
  showZones: boolean;
  selectedCoreLine: Collection<FeatureLike>;
  reorderCentroids?: Set<CoordinatePoint>;
  assignmentLoading: boolean;
  enterNewSampleId: boolean;
  gridLayerFeatures: GridLayerFeature[];
  overlayHidden: boolean;
  renameSampleId: boolean;
  togglingDrawSample: boolean;
  coresDialogOpen: boolean;
  coresForDialog: SoilCore[];
  /** Open per-sample core-line rotation dialog, if any (mapmaking grid line missions). */
  rotateCoreLineDialog: { sampleId: string; directionDeg: number } | null;
  selectableFeatures: KmlFeatureType[];
  lassoActionDialogOpen: boolean;
  lassoPolygonPoints?: Coordinate[][];
  lassoMoveInfo?: import('../../services/LassoService').MoveActionInfo;
  waitingForMoveClickCoordinates?: boolean;
  coordinateDisplayEnabled: boolean;
  currentLatitude: number | null;
  currentLongitude: number | null;
  technicianSidebarOpen: boolean;
}

export default class CommonMap extends PureComponent<CommonMapProps, CommonMapState> {
  SERIALIZABLE_STATE_VARIABLES: (keyof CommonMapState)[] = [
    'reorderLine',
    'renumber',
    'showZones',
    'drawConfig',
    'addSampleActive',
  ];

  SUPPORTED_ZONE_TYPES: KmlFeatureType[] = [
    KML_FEATURE_TYPE.PULLIN,
    KML_FEATURE_TYPE.SLOW,
    KML_FEATURE_TYPE.UNSAFE,
    KML_FEATURE_TYPE.FIELD,
    KML_FEATURE_TYPE.SAMPLE_ZONE,
    KML_FEATURE_TYPE.POLY,
    KML_FEATURE_TYPE.LASSO_ZONE,
  ];

  setStateAsync: SetStateAsync;
  shiftState: boolean;

  constructor(props: CommonMapProps) {
    super(props);

    const selectableFeatures: KmlFeatureType[] = [
      KML_FEATURE_TYPE.CENTROID,
      KML_FEATURE_TYPE.GENERIC_ZONE,
      KML_FEATURE_TYPE.SLOW,
      KML_FEATURE_TYPE.SAMPLE_ZONE,
      KML_FEATURE_TYPE.CORE_POINT,
      KML_FEATURE_TYPE.UNSAFE,
      KML_FEATURE_TYPE.COLLECTED_FIELD,
      KML_FEATURE_TYPE.PULLIN,
      KML_FEATURE_TYPE.PARKING_POINT,
      KML_FEATURE_TYPE.STARTING_POINT,
      KML_FEATURE_TYPE.END_POINT,
      KML_FEATURE_TYPE.PATH,
    ];

    if (!isCurrentUserOperator()) {
      selectableFeatures.push(KML_FEATURE_TYPE.FIELD);
    }

    this.state = {
      center: undefined,
      rotation: undefined,
      fit: undefined,
      zoom: undefined,
      deleteCandidate: null,
      zoneTypeChangeCandidate: null,
      skipCandidate: null,
      waypointCandidate: null,
      drawConfig: { geometryType: null, featureType: null, styleParams: {} },
      addSampleActive: null,
      parkingPointCandidate: null,
      startingPointCandidate: null,
      endPointCandidate: null,
      pullinPointCandidate: null,
      selectedSample: null,
      moveCandidate: null,
      reorderLine: [],
      selectedReorder: [],
      renumber: false,
      checkpoints: [],
      zoneMission: false,
      jobType: 'Grid',
      settingWaypoint: false,
      showZones: true,
      coreLine: [],
      selectedCoreLine: new Collection([]),
      reorderCentroids: new Set([]),
      assignmentLoading: false,
      enterNewSampleId: false,
      gridLayerFeatures: [],
      overlayHidden: false,
      renameSampleId: false,
      togglingDrawSample: false,
      coresDialogOpen: false,
      coresForDialog: [],
      rotateCoreLineDialog: null,
      selectableFeatures,
      lassoActionDialogOpen: false,
      lassoPolygonPoints: undefined,
      lassoMoveInfo: undefined,
      waitingForMoveClickCoordinates: false,
      coordinateDisplayEnabled: false,
      currentLatitude: null,
      currentLongitude: null,
      technicianSidebarOpen: false,
    };

    this.shiftState = false;

    this.zoomToMission = this.zoomToMission.bind(this);
    this.updateOrDeleteBoundary = this.updateOrDeleteBoundary.bind(this);
    this.updatePath = this.updatePath.bind(this);
    this.updatePoints = this.updatePoints.bind(this);
    this.updatePullin = this.updatePullin.bind(this);
    this.updateParkingPoint = this.updateParkingPoint.bind(this);
    this.updateStartingPoint = this.updateStartingPoint.bind(this);
    this.updateEndPoint = this.updateEndPoint.bind(this);
    this.resetDrawConfig = this.resetDrawConfig.bind(this);
    this.handleDraw = this.handleDraw.bind(this);
    this.handleModify = this.handleModify.bind(this);
    //this.addSampleAtPathEnd = this.addSampleAtPathEnd.bind(this);
    this.addSampleAfterPoint = this.addSampleAfterPoint.bind(this);
    this.handleSetWaypoint = this.handleSetWaypoint.bind(this);
    this.handleUndo = this.handleUndo.bind(this);
    this.updateSelectedReorder = this.updateSelectedReorder.bind(this);
    this.handleReorder = this.handleReorder.bind(this);
    this.handleKeydownCheckpoints = this.handleKeydownCheckpoints.bind(this);
    this.handleKeydownDelete = this.handleKeydownDelete.bind(this);
    this.getZoneType = this.getZoneType.bind(this);
    this.updateRenumber = this.updateRenumber.bind(this);
    this.cancelDrawingChange = this.cancelDrawingChange.bind(this);
    this.handleCoreLine = this.handleCoreLine.bind(this);
    this.handleCoreLineModify = this.handleCoreLineModify.bind(this);
    this.updateSelectedCoreLine = this.updateSelectedCoreLine.bind(this);
    this.closeOverlay = this.closeOverlay.bind(this);
    this.setStateAsync = setStateAsync.bind(this);
    this.updateGridLayerFeatures = this.updateGridLayerFeatures.bind(this);
    this.saveSamplePotentialPosition = this.saveSamplePotentialPosition.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleZoneTypeChange = this.handleZoneTypeChange.bind(this);
    this.handleDeleteCentroidOrCoreOrSampleZone = this.handleDeleteCentroidOrCoreOrSampleZone.bind(this);
    this.toggleIsFieldSelectable = this.toggleIsFieldSelectable.bind(this);
    this.togglePlaceParkingPoint = this.togglePlaceParkingPoint.bind(this);
    this.togglePlaceStartingPoint = this.togglePlaceStartingPoint.bind(this);
    this.togglePlaceEndPoint = this.togglePlaceEndPoint.bind(this);
    this.togglePlacePullinPoint = this.togglePlacePullinPoint.bind(this);
    this.toggleEraser = this.toggleEraser.bind(this);
    this.toggleCutter = this.toggleCutter.bind(this);
    this.handleLassoAction = this.handleLassoAction.bind(this);
    this.completeLassoMoveAction = this.completeLassoMoveAction.bind(this);
    this.toggleCoordinateDisplay = this.toggleCoordinateDisplay.bind(this);
    this.handleCoordinateChange = this.handleCoordinateChange.bind(this);
    this.handleCoordinateClick = this.handleCoordinateClick.bind(this);
  }

  componentDidMount() {
    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, this.getZoneType);
    EventBus.on(MAP_EVENTS.ZOOM_TO_MISSION, this.zoomToMission);
    this.getZoneType();
    this.deserializeState();
    document.addEventListener('keydown', this.handleKeydownDraw);
  }

  componentWillUnmount() {
    EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, this.getZoneType);
    EventBus.remove(MAP_EVENTS.ZOOM_TO_MISSION, this.zoomToMission);
    document.removeEventListener('keydown', this.handleKeydownCheckpoints);
    document.removeEventListener('keydown', this.handleKeydownDelete);
    document.removeEventListener('keydown', this.handleKeydownDraw);
    this.serializeState();
  }

  componentDidUpdate(prevProps: CommonMapProps, prevState: CommonMapState) {
    if (this.props.fit !== prevProps.fit) {
      this.setState({ fit: this.props.fit });
    }

    if (prevState.checkpoints !== this.state.checkpoints) {
      if (this.state.checkpoints.length && !prevState.checkpoints.length) {
        document.addEventListener('keydown', this.handleKeydownCheckpoints);
      } else if (prevState.checkpoints.length && !this.state.checkpoints.length) {
        document.removeEventListener('keydown', this.handleKeydownCheckpoints);
      }
    }

    if (prevState.deleteCandidate !== this.state.deleteCandidate) {
      if (this.state.deleteCandidate) {
        document.addEventListener('keydown', this.handleKeydownDelete);
      } else {
        document.removeEventListener('keydown', this.handleKeydownDelete);
      }
    }
  }

  toggleIsFieldSelectable() {
    this.setState((prevState) => {
      let selectableFeatures = [...prevState.selectableFeatures];
      const isFieldSelectable = prevState.selectableFeatures.includes(KML_FEATURE_TYPE.FIELD);

      if (isFieldSelectable) {
        selectableFeatures = selectableFeatures.filter((type) => type !== KML_FEATURE_TYPE.FIELD);
      } else {
        selectableFeatures.push(KML_FEATURE_TYPE.FIELD);
      }

      return { selectableFeatures };
    });
  }

  /**
   * serialize the state into session
   */
  serializeState() {
    const serializedState: { [key: string]: any } = {};

    for (const stateKey of this.SERIALIZABLE_STATE_VARIABLES) {
      serializedState[stateKey] = this.state[stateKey];
    }

    localStorage.setItem('CommonMapState', JSON.stringify(serializedState));
  }

  /**
   * deserialize the state
   */
  deserializeState() {
    const commonMapStateString = localStorage.getItem('CommonMapState');

    if (!commonMapStateString) {
      return;
    }

    const serializedState = JSON.parse(commonMapStateString);

    if (serializedState) {
      for (const stateKey of Object.keys(serializedState)) {
        this.setState(updateState(stateKey as keyof CommonMapState, serializedState[stateKey]));
      }
    }
  }

  /**
   * Updates gridLayerFeatures state
   * @param {array} gridLayerFeatures Array containing grid layer geo json features
   */
  updateGridLayerFeatures(gridLayerFeatures: GridLayerFeature[]) {
    this.setState({ gridLayerFeatures });
  }

  getZoneType() {
    const mission = getCurrentMission();
    const missionSpec = mission?.getPrimarySpec();

    if (missionSpec) {
      const zoneMission = missionSpec.pattern_type === GridPatterns.ZONE;
      const jobType: JobType = missionSpec.pattern_type === GridPatterns.ZONE ? 'Zone' : 'Grid';
      this.setState({ zoneMission, jobType });
    } else {
      this.setState({ zoneMission: false });
    }
  }

  handleKeydownDraw = (event: KeyboardEvent) => {
    if (event.ctrlKey) {
      switch (event.key) {
        case '1':
          this.toggleDrawUnsafeZone();
          break;
        case '2':
          this.toggleDrawSlowZone();
          break;
        case '3':
          this.toggleDrawGenericZone();
          break;
        case '4':
          this.toggleDrawReorder();
          break;
      }
    }

    this.shiftState = event.shiftKey;
  };

  handleKeydownCheckpoints(event: KeyboardEvent) {
    if (event.ctrlKey && event.key === 'z') {
      this.handleUndo();
    }
  }

  async handleKeydownDelete(event: KeyboardEvent) {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      await this.handleDelete();
    }
  }

  /**
   * Zooms to mission center
   */
  async zoomToMission() {
    const mission = getCurrentMission();

    if (!mission) {
      alertWarn('No mission loaded');

      return;
    }

    const { center, bounds } = mission.getMissionCenter();
    const centerInMapProjection = convertProjection4329(center);

    // Convert bounds from lon/lat to map projection (EPSG:3857)
    const bottomLeft = convertProjection4329([bounds.xmin, bounds.ymin]);
    const topRight = convertProjection4329([bounds.xmax, bounds.ymax]);
    const fieldExtent: Extent = [bottomLeft[0], bottomLeft[1], topRight[0], topRight[1]];

    dispatchSetExtent(fieldExtent);
    dispatchSetCenter(centerInMapProjection);
  }

  addDeleteCandidate = (deleteCandidate: SelectCandidate | null) => {
    this.setState({ deleteCandidate, selectedSample: deleteCandidate });
  };

  addZoneTypeChangeCandidate = (zoneTypeChangeCandidate: SelectCandidate | null) => {
    this.setState({ zoneTypeChangeCandidate });
  };

  addSkipCandidate = (skipCandidate: SelectCandidate | null) => {
    this.setState({ skipCandidate, selectedSample: skipCandidate });
  };

  addWaypointCandidate = (waypointCandidate: SelectCandidate | null) => {
    this.setState({ waypointCandidate, selectedSample: waypointCandidate });
  };

  addMoveCandidate = (moveCandidate: SelectCandidate | null) => {
    if (this.state.togglingDrawSample) {
      return;
    }

    this.setState({ moveCandidate, selectedSample: moveCandidate });
  };

  /**
   * The pending grid-line sample whose core line the popup may rotate, or
   * undefined when the current context/selection does not qualify
   * (see `canRotateCoreLine` — mapmaking, grid line pattern, pending sample
   * with every core still unpulled).
   */
  getRotatableCoreLineSample = (): Sample | undefined => {
    const candidate = this.state.skipCandidate;

    if (!candidate) {
      return undefined;
    }

    const sampleName = candidate.feature.getProperties()?.name;
    const sample = sampleName ? findSampleById(sampleName) : undefined;
    const spec = getCurrentMission()?.getPrimarySpec();

    const eligible = canRotateCoreLine({
      mapType: this.props.type,
      zoneMission: this.state.zoneMission,
      patternType: spec?.pattern_type ?? GridPatterns.NA,
      featureType: getFeatureType(candidate.feature.getProperties()),
      samplePending: Boolean(sample?.isPending()),
      allCoresUnpulled: Boolean(sample) && !sample?.hasCoresPulled(),
      coreCount: sample?.getSoilCores().length ?? 0,
    });

    return eligible ? sample : undefined;
  };

  openRotateCoreLineDialog = () => {
    const sample = this.getRotatableCoreLineSample();

    if (!sample) {
      return;
    }

    // Seed the dial with the direction the line is walked in; a never-generated
    // sample falls back to the mission's spec angle.
    const directionDeg = sample.get_core_line_direction_deg() ?? getCurrentMission()?.getPrimarySpec()?.angle ?? 0;

    this.setState({ rotateCoreLineDialog: { sampleId: sample.sample_id, directionDeg } });
  };

  closeRotateCoreLineDialog = () => {
    this.setState({ rotateCoreLineDialog: null });
  };

  handleRotateCoreLine = async (directionDeg: number) => {
    const dialog = this.state.rotateCoreLineDialog;

    if (!dialog) {
      return;
    }

    const sample = findSampleById(dialog.sampleId);

    this.setState({ rotateCoreLineDialog: null });

    if (!sample) {
      alertWarn(`Could not find sample ${dialog.sampleId} to rotate`);

      return;
    }

    this.addCheckpoint('Rotate Core Line');

    try {
      const rotated = await sample.rotate_core_line(directionDeg);

      if (rotated) {
        // The operation dispatches mission/core/path updates itself.
        alertSuccess(`Rotated the core line of sample ${sample.sample_id}`);
      } else {
        alertWarn(`Could not rotate the core line of sample ${sample.sample_id}`);
      }
    } catch (error) {
      console.error('Could not rotate core line', error);
      alertError(`Could not rotate the core line of sample ${sample.sample_id}`);
    }
  };

  handleSkip = async () => {
    if (!this.state.skipCandidate) {
      return;
    }

    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    const { feature, type } = this.state.skipCandidate;

    switch (type) {
      case KML_FEATURE_TYPE.CENTROID:
      case KML_FEATURE_TYPE.SAMPLE_ZONE:
      case KML_FEATURE_TYPE.CORE_POINT:
        const featureName = feature.getProperties().name;

        const sample = findSampleById(featureName);

        if (!sample) {
          alertWarn("Sample not found. Make sure you didn't unselect the sample too soon");

          return;
        }

        if (sample.bag_id) {
          const response = await alertWarnConfirm(
            `Warning! Sample ${sample.sample_id} already has a barcode. Skipping will DELETE this barcode. Are you sure? `,
          );

          if (!response) {
            return;
          }
        }

        const previousChangeType = sample?.change_type;

        const changeReasons: SampleChangeType[] = ['Skip', 'Delete', 'None'];
        const backupSamples = JSON.parse(normalizeDoubleEncodedJsonString(mission.backup_samples)) || [];

        if (backupSamples.length > 0) {
          changeReasons.push('Select Backup Point');
        }

        const selectorOptions: ShowChangeTypeSelectorPopup = {
          sampleCoreOrZoneId: featureName,
          changeTypeOptions: changeReasons,
          defaultChangeType: 'Skip',
        };
        const changeSampleResult = await promptForChangeReason(selectorOptions);

        if (changeSampleResult.result === 'confirm') {
          // if this isn't a request to move to a backup, and the skip type has changed...
          const changeTypeChanged = changeSampleResult.selectedChangeType !== previousChangeType;

          if (changeTypeChanged) {
            this.addCheckpoint(changeSampleResult.selectedChangeType ?? 'Unknown');

            sample.change_type = changeSampleResult.selectedChangeType ?? 'None';
            sample.change_reason = changeSampleResult.reason ?? '';

            if (sample.change_type !== 'Select Backup Point') {
              // get all sample waypoints and dispose
              if (sample) {
                await sample.removeFromPath();
              }

              sample.resetPulled();

              await logger.log('CommonMap - handleSkip - calculate_path_restore_cores');
              await mission.calculate_path_restore_cores([], null, false, true, true);
            }
          }

          if (sample.change_type === 'Select Backup Point') {
            const backupSamples = JSON.parse(normalizeDoubleEncodedJsonString(mission.backup_samples)) || [];

            if (!Array.isArray(backupSamples) || backupSamples.length === 0) {
              alertError('Map: no backup samples found');

              return;
            }

            const user = getCurrentUser();

            if (!user) {
              alertError('No current user found');

              return;
            }

            console.log('CommonMap - handleSkip: backupSamples', backupSamples, mission.backup_samples);

            const alertMessage = `Select Backup Point (${sample.sample_id} -> ${backupSamples[backupSamples.length - 1].sample_id})`;
            this.addCheckpoint(alertMessage);
            alertInfo(alertMessage);

            await mission.replaceSampleWithBackup(
              sample,
              user?.name ?? 'Unknown',
              changeSampleResult.reason ?? '',
              this.props.type === MAP_TYPES.SAMPLING ? 'sampling' : 'maps',
            );
          }

          if (sample.skipped_or_deleted) {
            try {
              const errMap = await setSampleBarcodes(sample.instance_id, '', true); // set samples in db
              console.log('CommonMap - handleSkip: errMap', errMap);
              // TODO should we do something with the error map?
            } catch (err) {
              await logger.log(`HANDLE_SKIP`, `sample.instance_id=${sample.instance_id}, err=${JSON.stringify(err)}`);
            }
          }
        }

        await this.closeOverlay();
        mission.to_ros_msg();
        dispatchMissionUpdated();
        // No blanket dispatchCoresNeedUpdate — each path fires its own:
        //   changeTypeChanged: calculate_path fires it internally
        //   Select Backup Point: replaceWithBackup fires dispatchSamplesUpdated
        //   unchanged/cancel: nothing changed, no dispatch needed
        break;
    }
  };

  /**
   * Handles feature deletion on the data model end based on feature type
   */
  handleDelete = async () => {
    if (!this.state.deleteCandidate) {
      return;
    }

    const { feature, type } = this.state.deleteCandidate;

    switch (type) {
      case KML_FEATURE_TYPE.CENTROID:
      case KML_FEATURE_TYPE.CORE_POINT:
      case KML_FEATURE_TYPE.SAMPLE_ZONE:
        const featureName = feature.getProperties().name;
        const zone_id = feature.getProperties().zone_id;
        await this.handleDeleteCentroidOrCoreOrSampleZone(featureName, zone_id);
        break;
      case KML_FEATURE_TYPE.FIELD:
      case KML_FEATURE_TYPE.GENERIC_ZONE:
      case KML_FEATURE_TYPE.UNSAFE:
      case KML_FEATURE_TYPE.SLOW:
      case KML_FEATURE_TYPE.COLLECTED_FIELD:
        const properties = feature.getProperties();
        // TODO need to revisit this type overlap
        // TODO potential maps bug with deletion
        await this.closeOverlay();
        await this.updateOrDeleteBoundary([], properties.zone_id);
        break;

      case KML_FEATURE_TYPE.PARKING_POINT:
        this.updateParkingPoint([0, 0]);
        await this.closeOverlay();
        // `this.updateParkingPoint` → `mission.update_parking_point`
        // → `dispatchStaticPointsUpdated()` already, so no additional
        // dispatch needed.
        break;

      case KML_FEATURE_TYPE.STARTING_POINT:
        this.updateStartingPoint([0, 0]);
        await this.closeOverlay();
        break;

      case KML_FEATURE_TYPE.END_POINT:
        this.updateEndPoint([0, 0]);
        await this.closeOverlay();
        break;

      default:
    }
  };

  handleZoneTypeChange = async (newZoneType: ZoneType) => {
    if (!this.state.zoneTypeChangeCandidate) {
      return;
    }

    const { feature, type } = this.state.zoneTypeChangeCandidate;

    switch (type) {
      case KML_FEATURE_TYPE.FIELD:
      case KML_FEATURE_TYPE.GENERIC_ZONE:
      case KML_FEATURE_TYPE.UNSAFE:
      case KML_FEATURE_TYPE.SLOW:
      case KML_FEATURE_TYPE.COLLECTED_FIELD:
        const properties = feature.getProperties();

        await this.closeOverlay();
        await this.updateZoneSinglePolyType(properties.zone_id, newZoneType);

        break;
      default:
    }
  };

  handleDeleteCentroidOrCoreOrSampleZone = async (featureName: string, zone_id?: number) => {
    console.log(`handleDeleteCentroidOrCore: ${featureName}`);
    const sample = findSampleById(featureName);
    const mission = getCurrentMission();

    if (this.props.type === MAP_TYPES.SAMPLING && sample?.sample_site_source !== 'Rogo Field Ops') {
      const selectorOptions: ShowChangeTypeSelectorPopup = {
        sampleCoreOrZoneId: featureName,
        changeTypeOptions: ['Skip', 'Delete', 'None'],
        defaultChangeType: 'Delete',
      };

      const sample = findSampleById(featureName);

      if (!sample) {
        alertWarn("Sample not found. Make sure you didn't unselect the sample too soon");

        return;
      }

      if (sample?.bag_id) {
        const response = await alertWarnConfirm(
          `Warning! Sample ${sample.sample_id} already has a barcode. Skipping will DELETE this barcode. Are you sure? `,
        );

        if (!response) {
          return;
        }
      }

      const previousChangeType = sample?.change_type;

      const changeSampleResult = await promptForChangeReason(selectorOptions);

      if (changeSampleResult.result === 'confirm') {
        if (changeSampleResult.selectedChangeType !== previousChangeType) {
          // TODO if changed, update path?
          // update path
          this.addCheckpoint(changeSampleResult.selectedChangeType?.toString() ?? 'Unknown');
          sample.change_type = changeSampleResult.selectedChangeType ?? 'None';
          sample.change_reason = changeSampleResult.reason ?? '';

          // get all sample waypoints and dispose
          if (sample && changeSampleResult.selectedChangeType !== 'None') {
            mission?.getWaypoints().forEach((wp) => {
              if (wp.getCorePoint()?.getSoilCore()?.Sample_id === sample.instance_id) {
                wp.dispose();
              }
            });
          }

          if (sample?.skipped_or_deleted) {
            try {
              const errMap = await setSampleBarcodes(sample?.instance_id, '', true); // set samples in db
              console.log('CommonMap - handleDeleteCentroidOrCore: errMap', errMap);
              // TODO should we do something with the error map?
            } catch (err) {
              await logger.log(`HANDLE_SKIP`, `sample.instance_id=${sample?.instance_id}, err=${JSON.stringify(err)}`);
            }
          }

          await logger.log('CommonMap - handleDeleteCentroidOrCoreOrSampleZone - calculate_path_restore_cores');
          await mission?.calculate_path_restore_cores([], null, false, true, true);
        }

        await this.closeOverlay();
        dispatchMissionUpdated();
        // calculate_path_restore_cores → calculate_path already fires
        // dispatchCoresNeedUpdate() internally.
      }
    } else {
      if (mission?.is_zone_mission() && zone_id) {
        // Deleting a sample zone boundary
        await this.updateOrDeleteBoundary([], zone_id);
      } else {
        // setting the coordinates to 0, 0 is actually how we tell the underlying function
        // to delete the point...
        console.log(`immediate delete`);
        await this.closeOverlay();
        await this.props.onLoading(async () => {
          await this.updatePoints([0.0, 0.0], featureName);
        });
      }
    }
  };

  /**
   * Resets the draw configuration
   */
  resetDrawConfig = () => {
    this.setState({
      drawConfig: { geometryType: null, featureType: null, styleParams: {}, zoneId: undefined },
    });
  };

  makeContainedPolygonsHoles = () => {
    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    this.addCheckpoint('Make Contained Polygons Holes');

    Zone.makeContainedPolygonsHoles(mission);
  };

  fixPolygons = () => {
    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    this.addCheckpoint('Fix polygons');

    Zone.fixPolygons(mission);
  };

  toggleDrawUnsafeZone = () => {
    if (this.state.drawConfig.featureType !== DRAW_TYPES.UNSAFE_ZONE) {
      this.setState({
        drawConfig: {
          geometryType: GeometryType.POLYGON,
          featureType: DRAW_TYPES.UNSAFE_ZONE,
          styleParams: { linestringColor: RED, pointColor: RED, polygonColor: RED.concat(0.3) },
        },
      });
    } else {
      this.resetDrawConfig();
    }
  };

  toggleDrawSlowZone = () => {
    if (this.state.drawConfig.featureType !== DRAW_TYPES.SLOW_ZONE) {
      this.setState({
        drawConfig: {
          geometryType: GeometryType.POLYGON,
          featureType: DRAW_TYPES.SLOW_ZONE,
          styleParams: { linestringColor: BROWN, pointColor: BROWN, polygonColor: BROWN.concat(0.3) },
        },
      });
    } else {
      this.resetDrawConfig();
    }
  };

  toggleDrawCores = () => {
    if (this.state.drawConfig.featureType !== DRAW_TYPES.CORE_LINE) {
      this.setState({
        drawConfig: {
          geometryType: GeometryType.LINE_STRING,
          featureType: DRAW_TYPES.CORE_LINE,
          styleParams: { linestringColor: BROWN, pointColor: BROWN, polygonColor: BROWN },
        },
      });
    } else {
      this.resetDrawConfig();
    }
  };

  toggleDrawGenericZone = () => {
    if (this.state.drawConfig.featureType !== DRAW_TYPES.PULLIN_ZONE) {
      this.setState({
        drawConfig: {
          geometryType: GeometryType.POLYGON,
          featureType: DRAW_TYPES.PULLIN_ZONE,
          styleParams: { linestringColor: GREEN, pointColor: GREEN, polygonColor: GREEN.concat(0.3) },
        },
      });
    } else {
      this.resetDrawConfig();
    }
  };

  toggleDrawLassoZone = () => {
    if (this.state.drawConfig.featureType !== DRAW_TYPES.LASSO_ZONE) {
      this.setState({
        drawConfig: {
          geometryType: GeometryType.POLYGON,
          featureType: DRAW_TYPES.LASSO_ZONE,
          styleParams: { linestringColor: RED, pointColor: RED, polygonColor: RED.concat(0.3) },
        },
      });
    } else {
      this.resetDrawConfig();
    }
  };

  toggleDrawFieldZone = () => {
    if (this.state.drawConfig.featureType !== DRAW_TYPES.FIELD_ZONE) {
      this.setState({
        drawConfig: {
          geometryType: GeometryType.POLYGON,
          featureType: DRAW_TYPES.FIELD_ZONE,
          styleParams: { linestringColor: YELLOW, pointColor: YELLOW, polygonColor: YELLOW.concat(0.3) },
        },
      });
    } else {
      this.resetDrawConfig();
    }
  };

  toggleDrawSampleZone = () => {
    if (this.state.drawConfig.featureType !== DRAW_TYPES.SAMPLE_ZONE) {
      this.setState({
        drawConfig: {
          geometryType: GeometryType.POLYGON,
          featureType: DRAW_TYPES.SAMPLE_ZONE,
          styleParams: { linestringColor: BLACK, pointColor: BLACK, polygonColor: BLACK.concat(0.3) },
        },
      });
    } else {
      this.resetDrawConfig();
    }
  };

  toggleDrawToAddPolyToZone = (zoneId: number) => {
    if (this.state.drawConfig.featureType !== DRAW_TYPES.POLY) {
      this.setState({
        drawConfig: {
          geometryType: GeometryType.POLYGON,
          featureType: DRAW_TYPES.POLY,
          styleParams: { linestringColor: BLACK, pointColor: BLACK, polygonColor: BLACK.concat(0.3) },
          zoneId,
        },
      });
    } else {
      this.resetDrawConfig();
    }
  };

  toggleDrawReorder = () => {
    if (this.state.drawConfig.featureType !== DRAW_TYPES.REORDER_LINE) {
      this.setState({
        drawConfig: {
          geometryType: GeometryType.LINE_STRING,
          featureType: DRAW_TYPES.REORDER_LINE,
          styleParams: { linestringColor: BURLY_WOOD, pointColor: BURLY_WOOD, polygonColor: BURLY_WOOD },
        },
      });
    } else {
      this.resetDrawConfig();
    }
  };

  toggleMoveCore = () => {
    if (this.state.drawConfig.featureType !== DRAW_TYPES.CORE_POINT) {
      this.setState({
        drawConfig: {
          geometryType: GeometryType.POINT,
          featureType: DRAW_TYPES.CORE_POINT,
          styleParams: { pointColor: PLUM },
          action: 'Moving',
        },
      });
    } else {
      this.resetDrawConfig();
    }
  };

  togglePlaceParkingPoint = () => {
    if (this.state.drawConfig.featureType === DRAW_TYPES.PARKING_POINT) {
      this.resetDrawConfig();

      return;
    }

    this.setState({
      drawConfig: {
        geometryType: GeometryType.POINT,
        featureType: DRAW_TYPES.PARKING_POINT,
        styleParams: { pointColor: LIGHT_BLUE },
      },
    });

    return true;
  };

  togglePlaceStartingPoint = () => {
    if (this.state.drawConfig.featureType === DRAW_TYPES.STARTING_POINT) {
      this.resetDrawConfig();

      return;
    }

    this.setState({
      drawConfig: {
        geometryType: GeometryType.POINT,
        featureType: DRAW_TYPES.STARTING_POINT,
        styleParams: { pointColor: GREEN },
      },
    });

    return true;
  };

  togglePlaceEndPoint = () => {
    if (this.state.drawConfig.featureType === DRAW_TYPES.END_POINT) {
      this.resetDrawConfig();

      return;
    }

    this.setState({
      drawConfig: {
        geometryType: GeometryType.POINT,
        featureType: DRAW_TYPES.END_POINT,
        styleParams: { pointColor: RED },
      },
    });

    return true;
  };

  togglePlacePullinPoint = () => {
    if (this.state.drawConfig.featureType === DRAW_TYPES.PULLIN_POINT) {
      this.resetDrawConfig();

      return;
    }

    this.setState({
      drawConfig: {
        geometryType: GeometryType.POINT,
        featureType: DRAW_TYPES.PULLIN_POINT,
        styleParams: { pointColor: GREEN },
      },
    });

    return true;
  };

  toggleEraser = async () => {
    if (this.state.drawConfig.featureType === DRAW_TYPES.ERASER) {
      this.resetDrawConfig();

      return;
    }

    this.setState({
      drawConfig: {
        geometryType: GeometryType.LINE_STRING,
        featureType: DRAW_TYPES.ERASER,
        styleParams: {
          pointColor: RED,
          linestringColor: RED,
          strokeWidth: 8,
          polygonColor: RED, // important, otherwise line won't show
        },
      },
    });

    return true;
  };

  toggleCutter = async () => {
    if (this.state.drawConfig.featureType === DRAW_TYPES.CUTTER) {
      this.resetDrawConfig();

      return;
    }

    this.setState({
      drawConfig: {
        geometryType: GeometryType.LINE_STRING,
        featureType: DRAW_TYPES.CUTTER,
        styleParams: {
          pointColor: '#FF6600',
          linestringColor: '#FF6600',
          strokeWidth: 6,
          polygonColor: '#FF6600', // important, otherwise line won't show
        },
      },
    });

    return true;
  };

  /**
   * Toggles the coordinate display feature on/off
   */
  toggleCoordinateDisplay = () => {
    this.setState((prevState) => ({
      coordinateDisplayEnabled: !prevState.coordinateDisplayEnabled,
      currentLatitude: !prevState.coordinateDisplayEnabled ? prevState.currentLatitude : null,
      currentLongitude: !prevState.coordinateDisplayEnabled ? prevState.currentLongitude : null,
    }));
  };

  /**
   * Handles coordinate changes from the CoordinateDisplayLayer
   * @param lat Latitude in EPSG:4326
   * @param lon Longitude in EPSG:4326
   */
  handleCoordinateChange = (lat: number | null, lon: number | null) => {
    this.setState({
      currentLatitude: lat,
      currentLongitude: lon,
    });
  };

  /**
   * Handles coordinate clicks from the CoordinateDisplayLayer
   * @param lat Latitude in EPSG:4326
   * @param lon Longitude in EPSG:4326
   */
  handleCoordinateClick = (lat: number, lon: number) => {
    // Coordinates are already copied to clipboard by the layer
    // This handler can be used for additional functionality if needed
  };

  /**
   * Process eraser line to remove points from polygons/paths that intersect with it.
   * Removes the section of the polygon/path that contains the inBetween point.
   * @param eraserCoordinates The coordinates of the eraser line drawn by the user
   */
  async processEraserLine(eraserCoordinates: Coordinate[]) {
    // Find which feature(s) are currently selected for erasing
    const deleteCandidate = this.state.deleteCandidate;

    if (!deleteCandidate) {
      alertWarn('No feature selected for erasing');

      return;
    }

    const { feature, type } = deleteCandidate;
    const supportedTypes: KmlFeatureType[] = [
      KML_FEATURE_TYPE.FIELD,
      KML_FEATURE_TYPE.GENERIC_ZONE,
      KML_FEATURE_TYPE.SLOW,
      KML_FEATURE_TYPE.UNSAFE,
      KML_FEATURE_TYPE.SAMPLE_ZONE,
      KML_FEATURE_TYPE.COLLECTED_FIELD,
    ];

    if (!supportedTypes.includes(type)) {
      alertWarn(`Eraser not supported for feature type: ${type}`);

      return;
    }

    // Get the zone_id from feature properties for boundary update
    const properties = feature.getProperties();
    const zoneId = properties.zone_id;

    if (!zoneId) {
      alertWarn('No zone_id found for the selected feature');

      return;
    }

    if (eraserCoordinates.length !== 3) {
      alertWarn('Eraser line must have 3 points');

      return;
    }

    const [firstPoint, inBetweenPoint, lastPoint] = eraserCoordinates;

    console.log('Erasing from feature type:', type);

    const geometry = feature.getGeometry();

    if (!geometry) {
      console.warn('Feature has no geometry');

      return;
    }

    const newCoordinates = polygonEraser.eraseFromPolygon(
      geometry as SimpleGeometry,
      firstPoint,
      inBetweenPoint,
      lastPoint,
    );

    if (!newCoordinates) {
      alertWarn('No intersection found between eraser line and feature geometry');

      return;
    }

    await this.updateOrDeleteBoundary(newCoordinates, zoneId);
  }

  /**
   * Process cutter line to split polygons into two separate polygons.
   * Creates two new zones from the original zone.
   * @param cutterCoordinates The coordinates of the cutter line drawn by the user
   */
  async processCutterLine(cutterCoordinates: Coordinate[]) {
    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    // Find which feature is currently selected for cutting
    const deleteCandidate = this.state.deleteCandidate;

    if (!deleteCandidate) {
      alertWarn('No feature selected for cutting');

      return;
    }

    const { feature, type } = deleteCandidate;
    const supportedTypes: KmlFeatureType[] = [
      KML_FEATURE_TYPE.FIELD,
      KML_FEATURE_TYPE.GENERIC_ZONE,
      KML_FEATURE_TYPE.SLOW,
      KML_FEATURE_TYPE.UNSAFE,
      KML_FEATURE_TYPE.SAMPLE_ZONE,
      KML_FEATURE_TYPE.COLLECTED_FIELD,
    ];

    if (!supportedTypes.includes(type)) {
      alertWarn(`Feature type ${type} is not supported for cutting`);

      return;
    }

    // Get the zone_id from feature properties
    const properties = feature.getProperties();
    const originalZoneId = properties.zone_id;

    if (!originalZoneId) {
      alertWarn('Feature does not have a zone_id');

      return;
    }

    await zonesManager.cutPoly(mission, feature, originalZoneId, cutterCoordinates);
  }

  /**
   * Toggle the draw sample draw config
   */
  toggleDrawSample = async (action: 'Moving' | 'Adding') => {
    this.setState({ togglingDrawSample: true });

    // TODO should have a better check condition here
    if (this.state.drawConfig.featureType !== DRAW_TYPES.CENTROID) {
      if (action === 'Moving') {
        const { feature } = this.state.moveCandidate!;
        const properties = feature.getProperties();
        const sampleId = properties.name;
        const sample = findSampleById(sampleId);

        // Check if sample (or related sample - for multi-lab scenario) has a barcode.
        const sampleWithBarcode = sample?.getSampleSite()?.getSampleCentroid()?.findSampleWithBarcode();

        if (sampleWithBarcode) {
          const confirmed = await alertWarnConfirm(
            `Warning! Sample ${sampleWithBarcode.sample_id} already has a barcode. Moving will DELETE this barcode. Are you sure? `,
          );

          if (!confirmed) {
            return false;
          }
        }
      }

      const job = getCurrentMission()?.getJob();
      const mapmaking = this.props.type !== MAP_TYPES.SAMPLING;

      if (job && mapmaking) {
        if (action === 'Adding' && !job.allowed_to_create) {
          if (
            !(await alertWarnConfirm(
              `${this.state.zoneMission ? 'Cores' : 'Samples'} are not allowed to be created for this job. Are you sure you want to continue?`,
            ))
          ) {
            this.setState({ togglingDrawSample: false });

            return false;
          }
        }
      }

      if (job && !mapmaking) {
        if (action === 'Moving' && !job.allowed_to_move) {
          if (
            !(await alertWarnConfirm(
              'Samples are not allowed to be moved for this job. Are you sure you want to continue?',
            ))
          ) {
            this.setState({ togglingDrawSample: false });

            return false;
          }
        }
      }

      this.setState({
        drawConfig: {
          geometryType: GeometryType.POINT,
          featureType: DRAW_TYPES.CENTROID,
          styleParams: { pointColor: PLUM },
          action,
        },
        togglingDrawSample: false,
      });

      return true;
    } else {
      this.resetDrawConfig();
      this.setState({ togglingDrawSample: false });

      return false;
    }
  };

  addCheckpoint = (name: string) => {
    const checkpoints = this.state.checkpoints.slice();
    checkpoints.push(createCheckpoint(name));
    this.setState({ checkpoints });
  };

  async updateOrDeleteBoundary(points: Coordinate[][], id: number) {
    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    this.addCheckpoint('Update or Delete Boundary');

    const newPoints = points.map((ring) =>
      removeConsecutiveDuplicateVertices(ring.map((coordSet) => convertProjection3857(coordSet))),
    );

    await mission.updateOrDeleteBoundary(id, newPoints);
    // update_or_delete_boundary fires BOUNDARY_UPDATED on both paths
    // (update + delete) and CORES_NEED_UPDATE on the delete path.
    dispatchMissionUpdated();
  }

  async createNewPolygon(points: Coordinate[][], polygonType: KmlFeatureType) {
    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    const newPoints = points.map((ring) =>
      removeConsecutiveDuplicateVertices(ring.map((coordSet) => convertProjection3857(coordSet))),
    );

    let zone: Zone | undefined;

    switch (polygonType) {
      case KML_FEATURE_TYPE.SAMPLE_ZONE:
        // Special case for creating a sample zone
        this.addCheckpoint('Create New Sample Zone');

        const { zone: newSampleZone, errorMessages } = await mission.generateSampleZone([
          newPoints.map((poly) => poly.map((p) => p.reverse())),
        ]);

        if (!newSampleZone) {
          alertError('Failed to make a field boundary a sample zone');

          errorMessages.forEach((errorMessage) => {
            alertWarn(errorMessage);
          });
        } else {
          zone = newSampleZone;
        }

        break;

      case KML_FEATURE_TYPE.POLY:
        this.addCheckpoint('Create New Polygon');

        // Add poly to existing zone
        zone = mission.getZones().find((z) => z.instance_id === this.state.drawConfig.zoneId);

        if (!zone) {
          break;
        }

        const newPoly = await zone.addNewPolyOrHole(newPoints);

        if (newPoly) {
          alertSuccess(`Added new poly "${newPoly.polyId}" to zone "${zone.zone_name}"`);
        }

        break;

      case KML_FEATURE_TYPE.LASSO_ZONE:
        // Store polygon points and open the lasso action dialog
        this.setState({
          lassoActionDialogOpen: true,
          lassoPolygonPoints: newPoints,
        });

        return;

      default:
        this.addCheckpoint('Create New Zone');
        zone = await mission.createNewRegularZone(polygonType, newPoints);
        break;
    }

    if (zone && !zone.hasValidBoundaries(true)) {
      alertWarn(`Zone "${zone.zone_name}" is invalid - check for overlaps and holes position`);
    }

    dispatchMissionBoundaryUpdated();
    dispatchMissionUpdated();
    // Creating a new polygon does not move or delete cores.
  }

  async updateZoneSinglePolyType(polyId: number, newZoneType: ZoneType) {
    console.log(`updateZonePolygonType: polyId=${polyId}, newZoneType=${newZoneType}`);

    this.addCheckpoint('Update Zone Type');

    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    if (newZoneType === ZoneType.SAMPLE_ZONE) {
      const movingFromZone = mission.getZoneByPolyId(polyId);

      if (!movingFromZone) {
        alertError(`Zone with poly Id ${polyId} not found`);

        return;
      }

      const { zone: newSampleZone, errorMessages } = await mission.generateSampleZoneFromZone(movingFromZone);

      if (!newSampleZone) {
        alertError('Failed to make a field boundary a sample zone');

        errorMessages.forEach((errorMessage) => {
          alertWarn(errorMessage);
        });

        return;
      }

      mission.disposeBoundaryByPolyId(polyId);
    } else {
      let newZone = mission.getZones().find((z) => z.zone_type === newZoneType);

      if (!newZone) {
        [newZone] = await Zone.new_zone(ZONE_TYPE_TO_KML_FEATURE_TYPE[newZoneType], [], mission.instance_id);
      }

      if (!newZone) {
        alertWarn('Failed to create or find zone of the requested type');

        return;
      }

      mission.addPolyToZone(polyId, newZone);
    }

    dispatchMissionBoundaryUpdated();
    dispatchMissionUpdated();
    // Updating a poly's zone type does not touch cores.
  }

  // who calls this?
  // true delete: await this.updatePoints([0.0, 0.0], featureName);
  // operator move: await this.updatePoints(newSampleCoordinates, sampleId, false);
  // case KML_FEATURE_TYPE.CENTROID: await this.props.updatePoints(coordinates, properties.name, true); break;
  // case KML_FEATURE_TYPE.CORE_POINT: await this.props.updatePoints(coordinates, properties.name, true); break;
  /**
   * Updates points in the data model
   * @param {Coordinate} point New points
   * @param {string} name The id
   */
  async updatePoints(point: Coordinate, name: string, { addCheckpoint = true, centroid = false } = {}) {
    // console.log(`updatePoints: points=${point}, name=${name}, addCheckpoint=${addCheckpoint}, centroid=${centroid}`);
    const t1 = performance.now();

    point = convertProjection3857(point);

    if (addCheckpoint) {
      this.addCheckpoint(isPointDelete(point) ? 'Delete Point' : 'Update Points');
    }

    const newPoints = [{ id: name, coords: { lat: point[0], lon: point[1] } }];
    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    await mission.update_points(newPoints, this.props.type === MAP_TYPES.SAMPLING);
    // `mission.update_points` → `PointsUpdater.update` already fires
    // both `dispatchMissionUpdated()` and the targeted
    // `dispatchSamplesUpdated([...])` (or `dispatchCoresNeedUpdate()`
    // in the renumber branch). No additional dispatch needed here.
    const job = mission.getJob();
    const mapmaking = this.props.type !== MAP_TYPES.SAMPLING;
    const deletingPoint = point[0] === 0.0 && point[1] === 0.0;

    if (job && mapmaking && !deletingPoint && !job.allowed_to_move) {
      alertWarn('This job does not allow moving samples. Please make sure any moves are safety critical');
    }

    const t2 = performance.now();

    console.log(`updatePoints: ${point} took ${t2 - t1} milliseconds`);
  }

  /**
   * Updates pullin point in the data model
   * @param {array} point New pullin point
   */
  updatePullin(point: Coordinate, addCheckpoint = true) {
    if (addCheckpoint) {
      this.addCheckpoint('Update Pullin');
    }

    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    mission.update_pullin(convertProjection3857(point));
  }

  updateParkingPoint(point: Coordinate, addCheckpoint = true) {
    if (addCheckpoint) {
      this.addCheckpoint('Update Parking Point');
    }

    const mission = getCurrentMission();
    mission?.update_parking_point(convertProjection3857(point));
  }

  updateStartingPoint(point: Coordinate, addCheckpoint = true) {
    if (addCheckpoint) {
      this.addCheckpoint('Update Starting Point');
    }

    const mission = getCurrentMission();
    mission?.update_starting_point(convertProjection3857(point));
  }

  updateEndPoint(point: Coordinate, addCheckpoint = true) {
    if (addCheckpoint) {
      this.addCheckpoint('Update End Point');
    }

    const mission = getCurrentMission();
    mission?.update_end_point(convertProjection3857(point));
  }

  /**
   * Updates the path points in the data model
   * @param {array} points New path points
   */
  async updatePath(points: Coordinate[], addCheckpoint = true) {
    console.log(`updatePath: points=${points}`);

    if (addCheckpoint) {
      this.addCheckpoint('Update Path');
    }

    const newPoints = points.map((coordSet) => convertProjection3857(coordSet));
    const mission = getCurrentMission();
    await mission?.update_path(newPoints);
    // `mission.update_path` already fires `dispatchMissionUpdated()`
    // + `dispatchCoresNeedUpdate()` internally.
  }

  /**
   * Adds a new sample in the data model
   * @param {array} point New sample point
   * @param {string} sampleId The new sample id
   */
  addSample = async (point: Coordinate, sampleId: string, sampleSource: SampleSource, addCheckpoint = true) => {
    if (addCheckpoint) {
      this.addCheckpoint(`Add Sample ${sampleId}`);
    }

    point = convertProjection3857(point);
    const newPoint = { coords: { lat: point[0], lon: point[1] } };
    const mission = getCurrentMission();
    await mission?.add_sample(newPoint, sampleId, sampleSource);
    this.setState({ addSampleActive: null });
    dispatchMissionUpdated();
    // Targeted rebuild: only the new sample needs to be added to
    // CorePointsLayer / CentroidsLayer featuresBySampleId.
    dispatchSamplesUpdated([sampleId]);
  };

  /**
   * Adds a new core in the data model
   * @param {array} point New core point
   * @param {string} coreId The new core id
   */
  addCore = async (point: Coordinate, coreId: string, sampleSource: SampleSource, addCheckpoint = true) => {
    if (addCheckpoint) {
      this.addCheckpoint(`Add Core ${coreId}`);
    }

    point = convertProjection3857(point);
    const newPoint = { coords: { lat: point[0], lon: point[1] } };
    const mission = getCurrentMission();
    const newCore = await mission?.add_core(newPoint, coreId, sampleSource);

    if (!newCore) {
      return;
    }

    this.setState({ addSampleActive: null });

    dispatchMissionUpdated();
    // Targeted rebuild: a core was added to this specific sample. Note
    // `sampleId` is the full core id the user typed (e.g. "126.123"); the
    // core lives on the PARSED sample ("126"), so target the rebuild at
    // the created core's real sample id. Dispatching the raw "126.123"
    // would rebuild a sample that doesn't exist, leaving the new core
    // invisible until a full rebuild (CU bug report).
    const addedSampleId = newCore.sample_id;

    if (addedSampleId) {
      dispatchSamplesUpdated([addedSampleId]);
    } else {
      // Defensive: a freshly created core always resolves a sample, but
      // never silently drop the rebuild if that invariant is violated.
      dispatchCoresNeedUpdate();
    }

    return newCore;
  };

  renameSample = async (newSampleId: string, addCheckpoint = true) => {
    if (addCheckpoint) {
      this.addCheckpoint(`Rename Sample To ${newSampleId}`);
    }

    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    const feature = this.state.skipCandidate?.feature;

    if (!feature) {
      return;
    }

    const sampleOrCoreIdString = feature?.getProperties()?.name;

    try {
      await mission.rename_sample(sampleOrCoreIdString, newSampleId);
    } catch (error) {
      alertError(`Error renaming sample: ${error}`);
    }

    this.setState({ addSampleActive: null, enterNewSampleId: false, renameSampleId: false });
    dispatchMissionUpdated();
    // Targeted rebuild on rename: rebuildForSamples removes the old
    // featuresBySampleId entry and builds the new one.
    dispatchSamplesUpdated([sampleOrCoreIdString, newSampleId]);
  };

  /**
   * Adds a new sample to the path in the data model directly after the currently selected point
   * (the currently selected point is stored in this.state.selectedSample)
   * @param {array} point New sample point
   * @param {string} sampleId The new sample id
   */
  async addSampleAfterPoint(
    point: Coordinate,
    sampleId: string,
    sampleSource: SampleSource,
    { addReason = '', addCheckpoint = true } = {},
  ) {
    if (!this.state.selectedSample) {
      alertError('No sample selected to add after');

      return;
    }

    console.log(
      `addSampleAfterPoint: point=${point}, sampleId=${sampleId}, this.state.selectedSample=`,
      this.state.selectedSample,
    );

    if (addCheckpoint) {
      this.addCheckpoint(`Add Sample ${sampleId} After ${this.state.selectedSample?.feature.getProperties().name}`);
    }

    const mission = getCurrentMission();

    if (!mission) {
      alertError('No mission found');

      return;
    }

    const _point = convertProjection3857(point);
    const existingSampleId = this.state.selectedSample?.feature.getProperties().name;
    const newPoint = { coords: { lat: _point[0], lon: _point[1] } };
    const centroidPath = mission.getCentroidPath();
    const selectedIndex = centroidPath.findIndex((sample) => sample.sample_id === existingSampleId);
    centroidPath.splice(selectedIndex + 1, 0, { coord: _point, sample_id: sampleId });
    console.log('centroidPath', centroidPath);

    // coords from selectedSample and centroidPath don't exactly match, but are very close
    // find the closest centroid in the centroidPath to find where to insert the new point
    // let closestCoordsIndex = 0;
    // let latDiff = Math.abs(centroidPath[0][0] - selectedSampleCoords[0]);
    // let lonDiff = Math.abs(centroidPath[0][1] - selectedSampleCoords[1]);
    // for (let i = 1; i < centroidPath.length; i++) {
    //     if (Math.abs(centroidPath[i][0] - selectedSampleCoords[0]) < latDiff &&
    //         Math.abs(centroidPath[i][1] - selectedSampleCoords[1]) < lonDiff) {
    //         closestCoordsIndex = i;
    //         latDiff = Math.abs(centroidPath[i][0] - selectedSampleCoords[0]);
    //         lonDiff = Math.abs(centroidPath[i][1] - selectedSampleCoords[1]);
    //     }
    // }
    //console.log(`closestCoordsIndex=${closestCoordsIndex}, centroidPath[closestCoordsIndex]=${centroidPath[closestCoordsIndex]}`);
    //centroidPath.splice(closestCoordsIndex + 1, 0, _point);

    const sample = await mission.add_sample(newPoint, sampleId, sampleSource);

    if (addReason) {
      sample.change_type = 'Add';
      sample.change_reason = addReason;
    }

    this.setState({ addSampleActive: null });
    await mission.reorder(
      centroidPath.map((sample) => sample.coord),
      false,
      this.props.type === MAP_TYPES.SAMPLING,
    );

    if (mission.autoRenumberingAllowedBySpecs()) {
      await mission.renumber_samples(this.props.type === MAP_TYPES.SAMPLING, true);
    }

    dispatchMissionUpdated();
    // reorder → calculate_path_restore_cores → calculate_path already
    // fires dispatchCoresNeedUpdate() internally.
  }

  /**
   * Handle for draw event that will create a new object in the data model based on type
   * @param {*} event
   */
  async handleDraw(event: BaseEvent, sampleCentroids?: Set<CoordinatePoint>) {
    if (!eventIsDrawEvent(event)) {
      return;
    }

    const { geometryType, featureType } = this.state.drawConfig;

    if (!featureType) {
      throw new Error(`No featureType set in drawConfig`);
    }

    const coords = (event.feature.getGeometry() as SimpleGeometry).getCoordinates();
    // console.log(
    //   `handleDraw: geometryType=${geometryType}, featureType=${featureType}, ${coords.length} coordinates}, sampleCentroids`,
    //   sampleCentroids,
    // );

    if (geometryType === GeometryType.POLYGON && this.SUPPORTED_ZONE_TYPES.includes(featureType)) {
      const feature = event.feature as Feature<Polygon>;
      const coordinates = feature.getGeometry()?.getCoordinates() ?? [];
      await this.createNewPolygon(coordinates, featureType);
      this.resetDrawConfig();
    } else if (geometryType === GeometryType.LINE_STRING && featureType === DRAW_TYPES.REORDER_LINE) {
      this.addCheckpoint(`Reorder Line`);
      const feature = event.feature as Feature<LineString>;
      const coordinates = feature.getGeometry()?.getCoordinates() ?? [];
      this.setState({ reorderLine: coordinates, renumber: this.state.renumber, reorderCentroids: sampleCentroids });
      this.resetDrawConfig();
    } else if (geometryType === GeometryType.LINE_STRING && featureType === DRAW_TYPES.CORE_LINE) {
      this.addCheckpoint(`Core Line`);
      const feature = event.feature as Feature<LineString>;
      const coordinates = feature.getGeometry()?.getCoordinates() ?? [];

      // make sure we "pop" the last layer drawing marker off the map to account for the double click
      EventBus.dispatch('DRAW_LAYER:REMOVE_LAST_MARKER');
      this.setState({ coreLine: coordinates });
      this.resetDrawConfig();
    } else if (geometryType === GeometryType.LINE_STRING && featureType === DRAW_TYPES.ERASER) {
      // Don't addCheckpoint here — processEraserLine → updateOrDeleteBoundary creates its own.
      // A duplicate checkpoint would leave an empty undo entry that falsely reports success.
      const feature = event.feature as Feature<LineString>;
      const coordinates = feature.getGeometry()?.getCoordinates() ?? [];

      // make sure we "pop" the last layer drawing marker off the map to account for the double click
      EventBus.dispatch('DRAW_LAYER:REMOVE_LAST_MARKER');

      await this.processEraserLine(coordinates);
      this.resetDrawConfig();
    } else if (geometryType === GeometryType.LINE_STRING && featureType === DRAW_TYPES.CUTTER) {
      this.addCheckpoint(`Cut Polygon`);
      const feature = event.feature as Feature<LineString>;
      const coordinates = feature.getGeometry()?.getCoordinates() ?? [];

      // make sure we "pop" the last layer drawing marker off the map to account for the double click
      EventBus.dispatch('DRAW_LAYER:REMOVE_LAST_MARKER');

      await this.processCutterLine(coordinates);
      this.resetDrawConfig();
    } else if (
      geometryType === GeometryType.POINT &&
      (featureType === DRAW_TYPES.CENTROID || featureType === DRAW_TYPES.CORE_POINT)
    ) {
      // we don't reset the drawing config or add a checkpoint here because we want the user
      // to be able to click around the map for the point they want to place
      // the target centroid. We will add a checkpoint and reset the drawing config once the
      // user accepts the change
      const feature = event.feature as Feature<Point>;
      const coordinates = feature.getGeometry()?.getCoordinates() ?? [];
      this.setState({ addSampleActive: coordinates });
    } else if (geometryType === GeometryType.POINT && featureType === DRAW_TYPES.PARKING_POINT) {
      const feature = event.feature as Feature<Point>;
      const coordinates = feature.getGeometry()?.getCoordinates() ?? [];
      this.setState({ parkingPointCandidate: coordinates });
    } else if (geometryType === GeometryType.POINT && featureType === DRAW_TYPES.STARTING_POINT) {
      const feature = event.feature as Feature<Point>;
      const coordinates = feature.getGeometry()?.getCoordinates() ?? [];
      this.setState({ startingPointCandidate: coordinates });
    } else if (geometryType === GeometryType.POINT && featureType === DRAW_TYPES.END_POINT) {
      const feature = event.feature as Feature<Point>;
      const coordinates = feature.getGeometry()?.getCoordinates() ?? [];
      this.setState({ endPointCandidate: coordinates });
    } else if (geometryType === GeometryType.POINT && featureType === DRAW_TYPES.PULLIN_POINT) {
      const feature = event.feature as Feature<Point>;
      const coordinates = feature.getGeometry()?.getCoordinates() ?? [];
      this.setState({ pullinPointCandidate: coordinates });
    } else {
      this.resetDrawConfig();
      throw new Error(`Unsupported draw configuration.\ngeometryType: ${geometryType}\nfeatureType: ${featureType}`);
    }
  }

  saveSamplePotentialPosition(coordinatesInEspg4326: Coordinate) {
    const coords3857 = fromLonLat(coordinatesInEspg4326);

    this.setState({ addSampleActive: coords3857 });
  }

  /**
   * Type guard to check if the event is a modify event
   * @param event  The event that triggered the modify
   * @returns  Whether the event is a modify event
   */
  eventIsModifyEvent(event: BaseEvent | ModifyEvent): event is ModifyEvent {
    return (event as ModifyEvent).features !== undefined;
  }

  /**
   * Handle for modifying reorder line
   * @param {*} event
   */
  handleModify(event: BaseEvent) {
    if (!this.eventIsModifyEvent(event)) {
      return;
    }

    console.log(`handleModify`);
    // because we know what we put this one, we know this geometry we receive is a LineString
    const line = event.features.getArray()[0].getGeometry() as LineString;
    const coordinates = line.getCoordinates();
    this.setState({ reorderLine: coordinates });
  }

  /**
   * Handle for modifying core line
   * @param {*} event
   */
  handleCoreLineModify(event: BaseEvent) {
    if (!this.eventIsModifyEvent(event)) {
      return;
    }

    // because we know what we put this one, we know this geometry we receive is a LineString
    const line = event.features.getArray()[0].getGeometry() as LineString;
    const coordinates = line.getCoordinates();
    this.setState({ coreLine: coordinates });
  }

  /**
   * Handle for setting the waypoint
   */
  async handleSetWaypoint() {
    // if we don't have a robot mission, we definitely aren't in manual
    const waypointFeature = this.state.waypointCandidate?.feature;
    const properties = waypointFeature?.getProperties();

    const doneCallback = () => this.setState({ settingWaypoint: false });

    this.setState({ settingWaypoint: true });

    const featureType = getFeatureType(properties);

    if (this.state.zoneMission) {
      if (featureType === KML_FEATURE_TYPE.SAMPLE_ZONE) {
        this.props.setZoneWaypoint &&
          (await this.props.setZoneWaypoint(
            properties?.name,
            null,
            this.constructor.name,
            doneCallback,
            this.state.waypointCandidate?.coordinates,
          ));
      } else if (featureType === KML_FEATURE_TYPE.CORE_POINT) {
        this.props.setZoneWaypoint &&
          (await this.props.setZoneWaypoint(
            null,
            properties?.name,
            this.constructor.name,
            doneCallback,
            this.state.waypointCandidate?.coordinates,
          ));
      }
    } else {
      this.props.setWaypoint &&
        (await this.props.setWaypoint(
          StartAt.GoToSample,
          properties?.name,
          null,
          this.constructor.name,
          doneCallback,
          this.state.waypointCandidate?.coordinates,
        ));
    }

    // TODO we had a Sentry error that waypointCandidate was null, but I'm not sure how. We'll at least
    // check to make sure it's not null/undefined before trying to call it
    this.state.waypointCandidate?.removeFunc();
    this.setState({
      waypointCandidate: null,
      deleteCandidate: null,
      zoneTypeChangeCandidate: null,
      skipCandidate: null,
      moveCandidate: null,
    });
  }

  /**
   * Handle for undoing an action
   */
  async handleUndo() {
    const { featureType } = this.state.drawConfig;

    if (featureType !== null) {
      if (featureType === DRAW_TYPES.REORDER_LINE) {
        this.setState({ reorderLine: this.state.reorderLine.slice(0, -1) });
        EventBus.dispatch('DRAW_LAYER:REMOVE_LAST_MARKER');
      } else if (featureType === DRAW_TYPES.CORE_LINE) {
        this.setState({ coreLine: this.state.coreLine.slice(0, -1) });
        EventBus.dispatch('DRAW_LAYER:REMOVE_LAST_MARKER');
      }
    } else if (this.state.checkpoints.length) {
      const checkpoints = this.state.checkpoints.slice();
      const checkpointId = checkpoints.pop();
      this.setState({ checkpoints });

      if (checkpointId === undefined) {
        alertWarn('No checkpoint to undo');

        return;
      }

      const checkpoint = await loadCheckpoint(checkpointId);

      // theoretically these have nothing to do with undos, but we want to make sure we clean up local state
      // so that we get an appropriate rerender of the map
      await this.cancelDrawingChange();
      dispatchMissionUpdated();
      // Undo reverts an arbitrary checkpoint — anything might have
      // changed, including cores. Fall back to a full rebuild.
      dispatchCoresNeedUpdate();
      dispatchMissionBoundaryUpdated();
      // Static points and zones may also have changed in the reverted
      // checkpoint.
      dispatchStaticPointsUpdated();
      dispatchZonesUpdated();
      alertSuccess(`Undo ${checkpoint.name} successful!`);
    }
  }

  /**
   * Updates the selected reorder line
   * @param {Collection<FeatureLike>} selectedReorder
   */
  updateSelectedReorder(selectedReorder: Coordinate[]) {
    console.log(`CommonMap.updateSelectedReorder: ${selectedReorder.length} features selected`);
    this.setState({ selectedReorder });
  }

  /**
   * Updates the selected core line
   * @param {Collection<FeatureLike>} selectedCoreLine
   */
  updateSelectedCoreLine(selectedCoreLine: Collection<FeatureLike>) {
    console.log(`CommonMap.updateSelectedCoreLine: ${selectedCoreLine.getLength()} features selected`);
    this.setState({ selectedCoreLine });
  }

  async cancelDrawingChange() {
    await this.closeOverlay();
    this.setState({
      coreLine: [],
      selectedReorder: [],
      selectedCoreLine: new Collection([]),
      reorderCentroids: new Set([]),
      addSampleActive: null,
      parkingPointCandidate: null,
      startingPointCandidate: null,
      endPointCandidate: null,
      pullinPointCandidate: null,
      selectedSample: null,
      reorderLine: [],
      enterNewSampleId: false,
    });
    EventBus.dispatch(`DRAW_LAYER:CLEAR_DRAWING_MARKERS`);
    this.resetDrawConfig();
  }

  async handleCoreLine() {
    const mission = getCurrentMission();
    const coordinates = this.state.coreLine.map((coord) => convertProjection3857(coord));
    console.log(`handleCoreLine: ${coordinates.length} coordinates`);

    if (!mission) {
      return;
    }

    if (!mission.getPrimarySpec()) {
      alertWarn('Handle core line - primary spec not found');

      return;
    }

    try {
      await mission.create_zone_cores(coordinates);
    } catch (e) {
      alertError(`Error creating zone cores: ${e}`);

      return;
    }

    this.setState({ coreLine: [], selectedCoreLine: new Collection([]) });
    EventBus.dispatch(`DRAW_LAYER:CLEAR_DRAWING_MARKERS`);
    dispatchMissionUpdated();
    // create_zone_cores creates new Sample + SoilCore records. We
    // don't know exactly which samples without walking the mission;
    // fall back to a full rebuild (infrequent user action).
    dispatchCoresNeedUpdate();
  }

  handleSampleChange = async () => {
    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    if (!this.state.addSampleActive) {
      await this.cancelDrawingChange();

      return;
    }

    const { action } = this.state.drawConfig;

    if (!action) {
      await this.cancelDrawingChange();

      return;
    }

    const moving = action === 'Moving';
    const selectedSampleId = moving ? this.state.moveCandidate?.feature?.getProperties()?.name : '';

    // const selectedSampleId = this.state.selectedSample?.feature?.getProperties()?.name;
    const addSampleId = mission.getNextSampleNumber();

    // TODO disallow move if skipped/deleted? should probably be checked in MapInteractionsLayer?
    const currentChangeIsMove = this.state.selectedSample?.feature?.getProperties()?.change_type === 'Move';
    const movingAndAlreadyMoved = moving && currentChangeIsMove;
    const isSamplingMap = this.props.type === MAP_TYPES.SAMPLING;
    let changeType: SampleChangeType = 'None';
    let changeReason = '';

    //const featureIsNotCorePoint = featureType !== KML_FEATURE_TYPE.CORE_POINT;
    if (!movingAndAlreadyMoved && isSamplingMap) {
      const result = await promptForChangeReason({
        changeTypeOptions: [action === 'Moving' ? 'Move' : 'Add'],
        defaultChangeType: 'Move',
        sampleCoreOrZoneId: action === 'Moving' ? selectedSampleId : addSampleId,
      });

      changeType = result.selectedChangeType ?? 'None';
      changeReason = result.reason ?? '';

      // TODO might need to handle 'extra' result, but for now
      // we only care about confirm
      if (result.result !== 'confirm') {
        await this.cancelDrawingChange();

        return;
      }
    }

    const newSampleCoordinates = [...this.state.addSampleActive];

    if (action === 'Moving') {
      if (!this.state.moveCandidate) {
        await this.cancelDrawingChange();

        return;
      }

      const { feature } = this.state.moveCandidate;
      const sampleId = feature?.getProperties()?.name;

      if (sampleId) {
        const sample = findSampleById(sampleId);

        if (sample) {
          this.addCheckpoint(`Move Sample ${sampleId}`);
          sample.change_type = changeType;
          sample.change_reason = changeReason;
          await this.updatePoints(newSampleCoordinates, sampleId, {
            addCheckpoint: false,
            centroid: !!feature?.getProperties()?.centroid,
          });
        } else {
          alertError(`Could not find sample with id ${sampleId}`);
        }
      }
    } else {
      if (this.props.type === MAP_TYPES.SAMPLING && addSampleId) {
        await this.addSampleAfterPoint(newSampleCoordinates, addSampleId.toString(), 'Rogo Field Ops', {
          addReason: changeReason,
        });
      } else {
        // check if the shift key is being held down
        if (!this.shiftState && !this.state.zoneMission && addSampleId) {
          await this.addSample(newSampleCoordinates, addSampleId.toString(), 'Rogo Map Maker');
        } else {
          this.setState({ enterNewSampleId: true });

          return;
        }
      }
      // this just resets all drawing states
    }

    await this.cancelDrawingChange();
    // `addSampleAfterPoint` / `addSample` already fire the right
    // events themselves — no additional dispatch needed.
  };

  handleParkingPointChange = async () => {
    const mission = getCurrentMission();

    if (!this.state.parkingPointCandidate || !mission) {
      await this.cancelDrawingChange();

      return;
    }

    this.updateParkingPoint(this.state.parkingPointCandidate);
    await this.cancelDrawingChange();
    // `mission.update_parking_point` already fires
    // `dispatchStaticPointsUpdated()`.
  };

  handleStartingPointChange = async () => {
    const mission = getCurrentMission();

    if (!this.state.startingPointCandidate || !mission) {
      await this.cancelDrawingChange();

      return;
    }

    this.updateStartingPoint(this.state.startingPointCandidate);
    await this.cancelDrawingChange();
  };

  handleEndPointChange = async () => {
    const mission = getCurrentMission();

    if (!this.state.endPointCandidate || !mission) {
      await this.cancelDrawingChange();

      return;
    }

    this.updateEndPoint(this.state.endPointCandidate);
    await this.cancelDrawingChange();
  };

  handlePullinPointChange = async () => {
    const mission = getCurrentMission();

    if (!this.state.pullinPointCandidate || !mission) {
      await this.cancelDrawingChange();

      return;
    }

    this.updatePullin(this.state.pullinPointCandidate);
    await this.cancelDrawingChange();
  };

  /**
   * Handles reorder
   */
  async handleReorder() {
    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    // console.log('RENUMBER', this.state.renumber);
    // if (this.state.reorderCentroids) {
    //     await mission.reorder2(this.state.reorderCentroids);
    //     this.setState({ reorderLine: [], selectedReorder: [] });
    //     return;
    // }
    const coordinates = this.state.reorderLine.map((coord) => convertProjection3857(coord));

    const errors = await mission?.reorder(
      coordinates,
      this.state.renumber,
      this.props.type === MAP_TYPES.SAMPLING,
      this.state.reorderCentroids,
    );

    // TODO use this new method once tested...
    //const errors = await mission.reorder2(this.state.reorderCentroids, false);
    if (errors?.length) {
      console.log(errors);
      const maxWarnings = 5;
      let count = 0;
      let noReorder = false;

      for (const error of errors) {
        if (error.includes('interleaved')) {
          // TODO allow admin to override?
          noReorder = true;
          alertError(error);
        } else {
          if (count < maxWarnings) {
            alertWarn(error);
            count++;
          }
        }
      }

      if (!noReorder) {
        alertSuccess('Successfully reordered path (noreorder)');
        this.setState({ reorderLine: [], selectedReorder: [] });
      }
    } else {
      alertSuccess('Successfully reordered path!');

      mission.alertIfPathInterleavingForRegularZone();

      this.setState({ reorderLine: [], selectedReorder: [] });
    }

    dispatchMissionUpdated();
    // reorder → calculate_path_restore_cores → calculate_path already
    // fires dispatchCoresNeedUpdate() internally.
    EventBus.dispatch(`DRAW_LAYER:CLEAR_DRAWING_MARKERS`);
  }

  updateRenumber(renumber: boolean) {
    this.setState({ renumber });
  }

  async closeOverlay() {
    this.state.deleteCandidate?.removeFunc();
    this.state.zoneTypeChangeCandidate?.removeFunc();
    this.state.waypointCandidate?.removeFunc();
    this.state.skipCandidate?.removeFunc();
    this.state.moveCandidate?.removeFunc();
    this.setState({
      deleteCandidate: null,
      zoneTypeChangeCandidate: null,
      waypointCandidate: null,
      skipCandidate: null,
      moveCandidate: null,
    });
  }

  handleLassoAction = async (action: LassoSampleAction) => {
    const mission = getCurrentMission();

    if (!mission || !this.state.lassoPolygonPoints) {
      this.setState({ lassoActionDialogOpen: false, lassoPolygonPoints: undefined });

      return;
    }

    const lassoService = LassoService.getInstance();

    if (action.name === 'Move') {
      // For Move action, we need to get the move info and wait for user to click new position
      const moveInfo = await lassoService.handleLassoAction(
        action,
        mission,
        this.state.lassoPolygonPoints,
        this.addCheckpoint,
      );

      if (moveInfo) {
        this.setState({
          lassoActionDialogOpen: false,
          lassoMoveInfo: moveInfo,
          waitingForMoveClickCoordinates: true,
        });
        alertInfo('Click on the map to select the new center position for the samples');
      } else {
        this.setState({ lassoActionDialogOpen: false, lassoPolygonPoints: undefined });
      }
    } else {
      // For other actions, execute immediately
      await lassoService.handleLassoAction(action, mission, this.state.lassoPolygonPoints, this.addCheckpoint);

      this.setState({
        lassoActionDialogOpen: false,
        lassoPolygonPoints: undefined,
      });
    }
  };

  completeLassoMoveAction = async (newCenterCoordinate: Coordinate) => {
    const mission = getCurrentMission();

    if (!mission || !this.state.lassoMoveInfo) {
      return;
    }

    const lassoService = LassoService.getInstance();
    const newCenter = convertProjection3857(newCenterCoordinate);

    await lassoService.completeMoveAction(this.state.lassoMoveInfo, newCenter, this.addCheckpoint);

    this.setState({
      lassoMoveInfo: undefined,
      waitingForMoveClickCoordinates: false,
      lassoPolygonPoints: undefined,
    });
  };

  get currentDrawType() {
    if (this.state.drawConfig.featureType !== null) {
      return this.state.drawConfig.featureType;
    }

    if (this.state.selectedReorder.length) {
      return DRAW_TYPES.REORDER_LINE;
    }

    if (this.state.coreLine.length) {
      return DRAW_TYPES.CORE_LINE;
    }

    if (this.state.addSampleActive) {
      // TOOD also core point??
      return DRAW_TYPES.CENTROID;
    }

    if (this.state.parkingPointCandidate) {
      return DRAW_TYPES.PARKING_POINT;
    }

    if (this.state.startingPointCandidate) {
      return DRAW_TYPES.STARTING_POINT;
    }

    if (this.state.endPointCandidate) {
      return DRAW_TYPES.END_POINT;
    }

    if (this.state.pullinPointCandidate) {
      return DRAW_TYPES.PULLIN_POINT;
    }

    // TOOD any other intermediate drawing objects??
    return undefined;
  }

  showPlotTool(mission: Mission | undefined): boolean {
    // missionActive is a string and can equal to "no id". Old logic would still display
    // the plot tool in this case. So, I'm preserving that behavior.
    // see Dashboard.tsx for more info
    if (!Boolean(this.props.missionActive)) {
      return false;
    }

    if (this.props.type === 'maps') {
      return true;
    }

    if (!mission) {
      return false;
    }

    return Boolean(mission.getJob()?.plot_mission);
  }

  render() {
    const mission = getCurrentMission();
    const robotConnected = this.props.connectionStatus === 'connected';
    const canSetWaypoint = this.state.waypointCandidate && robotConnected;
    const haveOverlayAction =
      this.state.deleteCandidate ||
      this.state.zoneTypeChangeCandidate ||
      this.state.skipCandidate ||
      this.state.moveCandidate ||
      canSetWaypoint;

    const drawing = this.currentDrawType !== undefined;
    const overlayVisible = haveOverlayAction && !drawing;
    const showPlotTool = this.showPlotTool(mission);
    const lassoService = LassoService.getInstance();
    // Rotation is offered only for a pending grid-line sample in mapmaking.
    const rotatableCoreLineSample = this.getRotatableCoreLineSample();

    return (
      <>
        {this.state.coresDialogOpen && this.props.openManualMeasurementDialog && (
          <CoresDialog
            handleClose={() =>
              this.setState({
                coresDialogOpen: false,
              })
            }
            cores={this.state.coresForDialog}
            openManualMeasurementDialog={this.props.openManualMeasurementDialog}
          />
        )}

        {this.state.lassoActionDialogOpen && (
          <LassoActionDialog
            open={this.state.lassoActionDialogOpen}
            title="Choose Action"
            message="What would you like to do with the samples in this zone?"
            actions={lassoService.getPossibleSampleActions(this.props.type)}
            onActionSelected={this.handleLassoAction}
          />
        )}
        {this.state.rotateCoreLineDialog && (
          <RotateCoreLineDialog
            open
            sampleId={this.state.rotateCoreLineDialog.sampleId}
            initialDirectionDeg={this.state.rotateCoreLineDialog.directionDeg}
            onClose={this.closeRotateCoreLineDialog}
            onApply={this.handleRotateCoreLine}
          />
        )}
        {this.state.coordinateDisplayEnabled && (
          <CoordinateDisplayOverlay latitude={this.state.currentLatitude} longitude={this.state.currentLongitude} />
        )}
        <BaseMapWithHistoricalLayers
          // fit={this.state.fit}
          mission={mission}
          historicalLayersService={this.props.historicalLayersService}
          absoluteOverlays={this.props.absoluteOverlays}
          trackingButton={this.props.trackingButton}
          enableSoilMap={this.props.type === MAP_TYPES.MAPS}
          showTimeline={this.props.showTimeline}
          overlays={[
            overlayVisible && (
              <MapOverlay
                key="mapOverLay"
                position={
                  this.state.deleteCandidate?.coordinates ||
                  this.state.zoneTypeChangeCandidate?.coordinates ||
                  this.state.waypointCandidate?.coordinates ||
                  this.state.skipCandidate?.coordinates ||
                  this.state.moveCandidate?.coordinates!
                }
                onClose={this.closeOverlay}
              >
                {/* Poly label + Add poly or hole button */}
                {this.props.type === MAP_TYPES.MAPS &&
                  this.state.deleteCandidate?.type &&
                  this.state.deleteCandidate.feature.getProperties()?.['poly_id'] &&
                  (() => {
                    if (!mission) {
                      return null;
                    }

                    const polyId = this.state.deleteCandidate.feature.getProperties()?.['poly_id'];

                    if (!polyId) {
                      return null;
                    }

                    const label = <Typography variant="body1">{`Poly "${polyId}"`}</Typography>;

                    const zone = mission.getZoneByPolyId(polyId);

                    if (!zone) {
                      return label;
                    }

                    return (
                      <>
                        {label}

                        <MapButton
                          onClick={() => this.toggleDrawToAddPolyToZone(zone.instance_id)}
                          tooltip={`Add poly or hole to zone ${zone.zone_name}`}
                        >
                          <MdOutlineDonutSmall size={24} color="rgb(155, 31, 233)" />
                        </MapButton>
                      </>
                    );
                  })()}

                {/* // TODO BARCODE this will now be used for manual on-screen navigation or auto nav? */}
                {this.state.waypointCandidate &&
                  this.props.connectionStatus === 'connected' &&
                  !featureIsSkippedOrDeleted(this.state.waypointCandidate.feature) && (
                    <WaypointSetButton
                      onClick={async () => {
                        await this.handleSetWaypoint();
                        await this.closeOverlay();
                      }}
                      loading={!!this.state.settingWaypoint}
                      textColor={'black'}
                    />
                  )}

                {this.state.waypointCandidate &&
                  !featureIsSkippedOrDeleted(this.state.waypointCandidate.feature) &&
                  this.props.type === MAP_TYPES.SAMPLING && (
                    <MapLoadingButton
                      tooltip="Select for scanning"
                      onClick={async () => {
                        const feature = this.state.waypointCandidate?.feature;

                        if (!feature) {
                          return;
                        }

                        const sampleOrCoreIdString = feature?.getProperties()?.name;

                        if (!sampleOrCoreIdString) {
                          return;
                        }

                        if (CoreCountEnforcementEnabled.get()) {
                          const sample = findSampleById(sampleOrCoreIdString);

                          if (!sample) {
                            return;
                          }

                          const { pulledCoresReady, requiredPulledCoresNumber, pulledCoresNumber } =
                            sample.hasPulledCoresReady();

                          if (!pulledCoresReady) {
                            alertWarn(
                              `Please record the right number of cores before scanning a barcode: (${requiredPulledCoresNumber} required, ${pulledCoresNumber} pulled)!`,
                            );

                            return;
                          }
                        }

                        if (sampleOrCoreIdString) {
                          await setSampleSelected({
                            sampleInstanceId: findSampleById(sampleOrCoreIdString)?.instance_id,
                            timestamp: new Date(),
                          });
                        }

                        await this.closeOverlay();
                      }}
                    >
                      <FaBarcode size={20} color={'black'} />
                    </MapLoadingButton>
                  )}

                {this.props.type === MAP_TYPES.MAPS &&
                  this.state.skipCandidate &&
                  // In grid missions show only to technicians
                  (this.props.accessLevel === 'Technician' || this.state.zoneMission) &&
                  // Do not show renaming icon if a core is clicked in zone mission
                  (!this.state.zoneMission ||
                    this.state.skipCandidate?.feature.getProperties()?.['poly_id'] !== undefined) && (
                    <MapLoadingButton
                      onClick={async () => {
                        const feature = this.state.skipCandidate?.feature;

                        if (!feature) {
                          return;
                        }

                        const sampleOrCoreIdString = feature?.getProperties()?.name;

                        if (!sampleOrCoreIdString) {
                          return;
                        }

                        const pointFeature = feature as Feature<Point>;
                        const coordinates = pointFeature.getGeometry()?.getCoordinates() ?? [];

                        this.setState({
                          enterNewSampleId: true,
                          renameSampleId: true,
                          addSampleActive: coordinates,
                        });
                      }}
                    >
                      <BiRename size={20} color={'black'} />
                    </MapLoadingButton>
                  )}

                {this.state.skipCandidate && <SkipButton onClick={this.handleSkip} textColor={'black'} />}

                {rotatableCoreLineSample && (
                  <MapButton
                    onClick={this.openRotateCoreLineDialog}
                    tooltip={`Rotate the core line of sample ${rotatableCoreLineSample.sample_id} (recalculating the path will reset it)`}
                  >
                    <TbRotateClockwise size={22} color={'black'} />
                  </MapButton>
                )}

                {this.state.deleteCandidate && (
                  <DeleteButton
                    onClick={this.handleDelete}
                    textColor={'black'}
                    // setting to false because now all this does in sampling mode
                    // is provides a popup which can be cancelled
                    confirm={false} //{this.props.type !== MAP_TYPES.MAPS}
                  />
                )}

                {this.props.type === MAP_TYPES.MAPS && this.state.zoneTypeChangeCandidate && (
                  <>
                    <MapButton onClick={() => this.handleZoneTypeChange(ZoneType.UNSAFE)} tooltip="Make Unsafe Zone">
                      <GiHazardSign size={24} color="rgb(164, 22, 35)" />
                    </MapButton>

                    <MapButton onClick={() => this.handleZoneTypeChange(ZoneType.SLOW)} tooltip="Make Slow Zone">
                      <GiHazardSign size={24} color="rgb(141, 103, 8)" />
                    </MapButton>

                    <MapButton onClick={() => this.handleZoneTypeChange(ZoneType.PULLIN)} tooltip="Make Generic Zone">
                      <BiPolygon size={24} color="rgb(29, 120, 29)" />
                    </MapButton>

                    <MapButton
                      onClick={() => this.handleZoneTypeChange(ZoneType.COLLECTED_FIELD)}
                      tooltip="Make Collected Field Zone"
                    >
                      <MdLocationSearching size={24} />
                    </MapButton>

                    <MapButton
                      onClick={() => this.handleZoneTypeChange(ZoneType.FIELD)}
                      tooltip="Make Field Boundary Zone"
                    >
                      <PiRectangle size={24} color="rgb(255, 165, 5)" />
                    </MapButton>

                    <MapButton
                      onClick={() => this.handleZoneTypeChange(ZoneType.SAMPLE_ZONE)}
                      tooltip="Make Sample Zone"
                    >
                      <FaNfcDirectional size={24} color="rgb(255, 2, 255)" />
                    </MapButton>
                  </>
                )}

                {this.state.moveCandidate &&
                  this.props.type === MAP_TYPES.SAMPLING &&
                  !featureIsSkippedOrDeleted(this.state.moveCandidate.feature) && (
                    <MapLoadingButton
                      onClick={async () => {
                        console.log('Moving');

                        if (!this.state.moveCandidate) {
                          alertError('No sample selected to move!');

                          return;
                        }

                        const { feature } = this.state.moveCandidate;
                        const properties = feature.getProperties();
                        const type = getFeatureType(properties);

                        if (type === KML_FEATURE_TYPE.CORE_POINT) {
                          this.toggleMoveCore();
                        } else if (type === KML_FEATURE_TYPE.CENTROID) {
                          await this.toggleDrawSample('Moving');
                        }
                      }}
                    >
                      <BsArrowsMove size={20} color={'black'} />
                    </MapLoadingButton>
                  )}

                {this.state.deleteCandidate?.type === DRAW_TYPES.CENTROID &&
                  !this.state.zoneMission && // TODO SVH FIX THIS TO USE STATE INSTEAD OF PROPS
                  this.props?.type === MAP_TYPES.SAMPLING &&
                  !featureIsSkippedOrDeleted(this.state.deleteCandidate.feature) && (
                    <MapButton
                      key={'AddPointMapButton'}
                      onClick={async () => {
                        this.setState({
                          deleteCandidate: null,
                          waypointCandidate: null,
                          skipCandidate: null,
                          moveCandidate: null,
                        });
                        await this.toggleDrawSample('Adding');
                      }}
                      style={{
                        backgroundColor:
                          this.state.drawConfig.featureType === DRAW_TYPES.CENTROID ? '#e2e0e0' : undefined,
                      }}
                      tooltip="Add Sample Point"
                    >
                      <IoMdAddCircleOutline size={22} color={'black'} />
                    </MapButton>
                  )}

                {this.props.openManualMeasurementDialog &&
                  this.state.selectedSample &&
                  mission &&
                  mission.isManualMeasurementRequired() && (
                    <MapButton
                      key={'CoresInfoButton'}
                      onClick={async () => {
                        if (!this.state.selectedSample) {
                          return;
                        }

                        const sampleID = this.state.selectedSample.feature.getProperties()?.name;
                        const sample = findSampleById(sampleID);

                        if (!sample) {
                          return;
                        }

                        this.setState({
                          coresDialogOpen: true,
                          coresForDialog: sample.getSoilCores(),
                        });
                      }}
                      tooltip="Cores info"
                    >
                      <TbShovel size={22} color={'black'} />
                    </MapButton>
                  )}

                {this.state.moveCandidate &&
                  this.props.type === MAP_TYPES.SAMPLING &&
                  !featureIsSkippedOrDeleted(this.state.moveCandidate.feature) && (
                    <MapLoadingButton
                      tooltip={'Move Sample to Robot Position'}
                      onClick={async () => {
                        try {
                          if (!this.state.moveCandidate) {
                            return;
                          }

                          const { feature } = this.state.moveCandidate;
                          const properties = feature.getProperties();
                          const sampleId = properties.name;

                          const selectorOptions: ShowChangeTypeSelectorPopup = {
                            sampleCoreOrZoneId: sampleId,
                            changeTypeOptions: ['Move'],
                            defaultChangeType: 'Move',
                          };

                          const mission = getCurrentMission();

                          if (!mission) {
                            alertError('No mission available');

                            return;
                          }

                          let changeType: SampleChangeType = 'Move';
                          let changeReason: ChangeReason | string = '';

                          if (mission.isLoamMission()) {
                            changeReason = 'Old Core Location';
                          }

                          const changeSampleResult = await promptForChangeReason(selectorOptions);

                          if (changeSampleResult.result !== 'confirm') {
                            return;
                          }

                          changeType = changeSampleResult.selectedChangeType ?? 'None';
                          changeReason = changeSampleResult.reason ?? '';

                          const sample = findSampleById(sampleId);

                          if (!sample) {
                            alertError('Could not find sample to move');

                            return;
                          }

                          const positionMessage = EventBus.recent(rosEventKey(INBOUND_TOPICS.POSITION));
                          const position: RosPosition = positionMessage?.detail?.msg;

                          if (!position) {
                            alertError('No robot position available');

                            return;
                          }

                          const newSampleCoordinates = convertProjection4329([position.y, position.x]);

                          if (!newSampleCoordinates) {
                            alertError('Could not convert robot position to map coordinates');

                            return;
                          }

                          const armPosition = calculateArmPosition(newSampleCoordinates, -position.z, '4329');
                          sample.change_type = changeType;
                          sample.change_reason = changeReason;
                          await this.updatePoints(armPosition, sampleId, {
                            addCheckpoint: true,
                            centroid: !!feature?.getProperties()?.centroid,
                          });
                        } catch (error) {
                          alertError(error?.toString() ?? 'Error moving sample');
                        } finally {
                          await this.closeOverlay();
                        }
                      }}
                    >
                      <GiMultipleTargets size={20} color={'black'} />
                    </MapLoadingButton>
                  )}
              </MapOverlay>
            ),
          ]}
          layers={[
            mission && (
              <MapInteractionsLayer
                key={'MapInteractionsLayer'}
                updatePath={this.updatePath}
                updatePoints={this.updatePoints}
                updateBoundary={this.updateOrDeleteBoundary}
                updatePullin={this.updatePullin}
                updateParkingPoint={this.updateParkingPoint}
                updateStartingPoint={this.updateStartingPoint}
                updateEndPoint={this.updateEndPoint}
                // TODO these should just become a single function with an additional parameter
                // really we can just have a "selected" with an array of properties on it
                // because this almost always ends up just being the same thing over and over again
                addDeleteCandidate={this.addDeleteCandidate}
                addZoneTypeChangeCandidate={this.addZoneTypeChangeCandidate}
                addSkipCandidate={this.addSkipCandidate}
                addWaypointCandidate={this.addWaypointCandidate}
                addMoveCandidate={this.addMoveCandidate}
                selectInteractionDisabled={!!this.state.drawConfig.geometryType || !!this.state.reorderLine.length}
                modifyInteractionDisabled={!!this.state.drawConfig.geometryType || !!this.state.reorderLine.length}
                zoneMission={this.state.zoneMission}
                jobType={this.state.jobType}
                showZones={this.state.showZones} // && (this.currentDrawType !== DRAW_TYPES.CORE_LINE)}
                type={this.props.type}
                selectableFeatures={this.state.selectableFeatures}
              />
            ),
            // Step 1 of the KmlLayer split: core point features live in a
            // dedicated sibling layer so a core completion (Shift+C) only
            // rebuilds the affected sample's cores, not the full map.
            // See docs/superpowers/plans/2026-04-08-kml-layer-split.md.
            mission && (
              <CorePointsLayer
                key={'CorePointsLayer'}
                jobType={this.state.jobType}
                zoneMission={this.state.zoneMission}
                type={this.props.type}
                addDeleteCandidate={this.addDeleteCandidate}
                addSkipCandidate={this.addSkipCandidate}
                addWaypointCandidate={this.addWaypointCandidate}
                addMoveCandidate={this.addMoveCandidate}
                selectInteractionDisabled={!!this.state.drawConfig.geometryType || !!this.state.reorderLine.length}
                modifyInteractionDisabled={!!this.state.drawConfig.geometryType || !!this.state.reorderLine.length}
              />
            ),
            // Step 2 of the KmlLayer split: sample centroids live in their
            // own sibling layer so barcode scans, active-sample changes,
            // and present-sample arrivals only touch centroid JSX.
            mission && (
              <CentroidsLayer
                key={'CentroidsLayer'}
                jobType={this.state.jobType}
                zoneMission={this.state.zoneMission}
                type={this.props.type}
              />
            ),
            // Step 3 of the KmlLayer split: waypoint path + snap points.
            mission && <PathLayer key={'PathLayer'} zoneMission={this.state.zoneMission} />,
            // Step 3 of the KmlLayer split: debug-only soil dump markers.
            mission && <DumpEventsLayer key={'DumpEventsLayer'} zoneMission={this.state.zoneMission} />,
            // Step 5 of the KmlLayer split: pullin, parking, starting,
            // end point. Reads directly from the mission, no to_feature.
            mission && <StaticPointsLayer key={'StaticPointsLayer'} />,
            // Step 6 of the KmlLayer split: all zones (field,
            // collected_field, sample_zone, visual_zone, slow, unsafe,
            // generic_zone). Reads mission.getZones() directly.
            mission && (
              <ZonesLayer
                key={'ZonesLayer'}
                showZones={this.state.showZones}
                jobType={this.state.jobType}
                type={this.props.type}
              />
            ),
            mission && (
              <DrawLayer
                key={'DrawLayer'}
                handleDraw={this.handleDraw}
                handleModify={this.handleModify}
                handleCoreLineModify={this.handleCoreLineModify}
                updateSelectedReorder={this.updateSelectedReorder}
                updateSelectedCoreLine={this.updateSelectedCoreLine}
                selectedCoreLine={this.state.selectedCoreLine}
                reorderLine={this.state.reorderLine}
                placedSample={this.state.addSampleActive}
                parkingPointCandidate={this.state.parkingPointCandidate}
                startingPointCandidate={this.state.startingPointCandidate}
                endPointCandidate={this.state.endPointCandidate}
                pullinPointCandidate={this.state.pullinPointCandidate}
                confirmPullinPoint={this.handlePullinPointChange}
                coreLine={this.state.coreLine}
                selectedReorder={this.state.selectedReorder}
                drawConfig={this.state.drawConfig}
              />
            ),
            <GridLayer key="GridLayer" gridLayerFeatures={this.state.gridLayerFeatures} />,
            <DrawingMarkersLayer
              key={'ReorderMarkersLayer'}
              drawConfig={this.state.drawConfig}
              currentDrawType={this.currentDrawType}
              showZones={this.state.showZones}
            />,
            <LassoMoveClickLayer
              key={'LassoMoveClickLayer'}
              waitingForClick={this.state.waitingForMoveClickCoordinates || false}
              onMapClick={this.completeLassoMoveAction}
              moveInfo={this.state.lassoMoveInfo}
            />,
            <CoordinateDisplayLayer
              key={'CoordinateDisplayLayer'}
              enabled={this.state.coordinateDisplayEnabled}
              onCoordinateChange={this.handleCoordinateChange}
              onClick={this.handleCoordinateClick}
            />,
            ...(this.props.layers || []),
          ]}
          buttons={[
            mission && <CenterMissionButton key={'CenterMissionButton'} onClick={this.zoomToMission} />,
            <CoordinateDisplayButton
              key={'CoordinateDisplayButton'}
              onClick={this.toggleCoordinateDisplay}
              toggled={this.state.coordinateDisplayEnabled}
            />,
            mission && (
              <SamplingParametersButton
                key={'SamplingParametersButton'}
                zoneMission={this.state.zoneMission}
                accessLevel={this.props.accessLevel}
                type={this.props.type}
                addCheckpoint={this.addCheckpoint}
              />
            ),
            this.state.zoneMission && (
              <MapButton
                key={'ShowZonesMapButton'}
                tooltip={this.state.showZones ? 'Hide Zones' : 'Show Zones'}
                toggled={this.state.showZones}
                onClick={() => this.setState({ showZones: !this.state.showZones })}
              >
                <DiCelluloid size={36} />
              </MapButton>
            ),
            mission && (
              <DrawButton
                key={'DrawButton'}
                drawConfig={this.state.drawConfig}
                addSampleActive={this.state.addSampleActive}
                coreLine={this.state.coreLine}
                reorderLine={this.state.reorderLine}
                addSample={this.addSample}
                addCore={this.addCore}
                addSampleAfterPoint={this.addSampleAfterPoint}
                mapType={this.props.type}
                zoneMission={this.state.zoneMission}
                renumber={this.state.renumber}
                cancelAddSample={this.cancelDrawingChange}
                makeContainedPolygonsHoles={this.makeContainedPolygonsHoles}
                fixPolygons={this.fixPolygons}
                toggleDrawGenericZone={this.toggleDrawGenericZone}
                toggleDrawLassoZone={this.toggleDrawLassoZone}
                toggleDrawFieldZone={this.toggleDrawFieldZone}
                toggleDrawSampleZone={this.toggleDrawSampleZone}
                toggleDrawToAddPolyToZone={this.toggleDrawToAddPolyToZone}
                renameSampleId={this.state.renameSampleId}
                // TODO not totally happy with this implemention, we should be passing in the existing
                // sample ID somehow since we know it at the time we are doing a drawing command (assuming
                // we are editing and not moving a core or sample)
                renameSample={this.renameSample}
                deleteAllCoresOrSamples={() => {
                  if (this.state.zoneMission) {
                    this.addCheckpoint('Delete All Cores');
                    mission.delete_zone_cores();
                  } else {
                    this.addCheckpoint('Delete All Samples');
                    mission.delete_all_samples_and_cores();
                  }
                }}
                toggleDrawReorder={this.toggleDrawReorder}
                toggleDrawSlowZone={this.toggleDrawSlowZone}
                toggleDrawCores={this.toggleDrawCores}
                updateRenumber={this.updateRenumber}
                toggleDrawUnsafeZone={this.toggleDrawUnsafeZone}
                toggleDrawSample={this.toggleDrawSample}
                togglePlaceParkingPoint={this.togglePlaceParkingPoint}
                togglePlaceStartingPoint={this.togglePlaceStartingPoint}
                togglePlaceEndPoint={this.togglePlaceEndPoint}
                togglePlacePullinPoint={this.togglePlacePullinPoint}
                addCheckpoint={this.addCheckpoint}
                enterNewSampleId={this.state.enterNewSampleId}
                accessLevel={this.props.accessLevel}
                isFieldSelectable={this.state.selectableFeatures.includes(KML_FEATURE_TYPE.FIELD)}
                toggleIsFieldSelectable={this.toggleIsFieldSelectable}
                toggleEraser={this.toggleEraser}
                toggleCutter={this.toggleCutter}
                zoneTypeChangeCandidate={this.state.zoneTypeChangeCandidate}
                popperOffset={this.props.drawButtonPopperOffset}
              />
            ),
            Boolean(this.props.missionActive) && this.props.type === 'maps' && (
              <GridGenerationButton
                key="GridGenerationButton"
                updateGridLayerFeatures={this.updateGridLayerFeatures}
                gridLayerFeatures={this.state.gridLayerFeatures}
              />
            ),
            showPlotTool && (
              <PlotGenerationButton
                key="PlotGenerationButton"
                addCheckpoint={this.addCheckpoint}
                updateGridLayerFeatures={this.updateGridLayerFeatures}
                gridLayerFeatures={this.state.gridLayerFeatures}
                mapType={this.props.type}
                accessLevel={this.props.accessLevel}
              />
            ),
            ...(this.props.buttons || []),
            this.props.accessLevel === 'Technician' && (
              <TechnicianSidebarButton
                key="TechnicianSidebarButton"
                toggled={this.state.technicianSidebarOpen}
                onClick={() => this.setState((s) => ({ technicianSidebarOpen: !s.technicianSidebarOpen }))}
              />
            ),
            mission && this.state.checkpoints.length > 0 && <UndoButton key={'UndoButton'} onClick={this.handleUndo} />,
            (this.state.reorderLine.length > 0 ||
              this.state.coreLine.length > 0 ||
              this.state.addSampleActive ||
              this.state.parkingPointCandidate ||
              this.state.startingPointCandidate ||
              this.state.endPointCandidate ||
              this.state.pullinPointCandidate) && (
              <AssignmentButton
                key={'AssignmentButton'}
                loading={this.state.assignmentLoading}
                onClick={async () => {
                  if (this.state.reorderLine.length > 0) {
                    await this.handleReorder();
                  } else if (this.state.coreLine.length > 0) {
                    await this.handleCoreLine();
                  } else if (this.state.parkingPointCandidate) {
                    await this.handleParkingPointChange();
                  } else if (this.state.startingPointCandidate) {
                    await this.handleStartingPointChange();
                  } else if (this.state.endPointCandidate) {
                    await this.handleEndPointChange();
                  } else if (this.state.pullinPointCandidate) {
                    await this.handlePullinPointChange();
                  } else {
                    await this.handleSampleChange();
                  }
                }}
              />
            ),
            (this.state.reorderLine.length > 0 ||
              this.state.coreLine.length > 0 ||
              this.state.addSampleActive ||
              this.state.drawConfig.geometryType !== null) && (
              <DeselectAllButton key={'DeselectAllButton'} onClick={this.cancelDrawingChange} />
            ),
            this.state.drawConfig.geometryType !== null &&
              this.state.drawConfig.featureType === DRAW_TYPES.CENTROID && (
                <CoordinateInput key="samplePotentialPositionUpdater" onSave={this.saveSamplePotentialPosition} />
              ),
          ]}
          secondRowElements={this.props.secondRowElements ?? undefined}
          statusItems={this.props.statusItems}
          primaryActions={this.props.primaryActions}
        />
        {this.props.accessLevel === 'Technician' && (
          <TechnicianSidebar
            open={this.state.technicianSidebarOpen}
            onClose={() => this.setState({ technicianSidebarOpen: false })}
          />
        )}
      </>
    );
  }
}
